import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { TorneiV2List, TorneoDettaglioV2 } from './TorneiV2';
import { TorneiV3List, TorneoDettaglioV3, ImportedTournamentDetail } from './TorneiV3';
import { SchedaGiocatoreV2, DashboardStatsClubV2, AlboOro } from './StatisticheV2';
import { FinanzeClub } from './FinanzeClub';
import { CalendarioClub } from './CalendarioClub';
import { PartitaSingola } from './PartitaSingola';
import { useConfirmDialog } from './components/ui/ConfirmDialog';
import ImportTorneoAI from './components/club/ImportTorneoAI';
import ImportTorneoFromLink from './components/club/ImportTorneoFromLink';
import PersonalizzaClub from './components/club/PersonalizzaClub';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// ============================================
// 📊 MINI GRAFICI DASHBOARD
// ============================================

const CHART_COLORS = {
  primary: '#3B82F6',
  secondary: '#8B5CF6',
  success: '#10B981',
  warning: '#F59E0B',
  bars: ['#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#DBEAFE']
};

const MiniTrendChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-32 flex items-center justify-center text-gray-400 text-sm">
        Nessun dato disponibile
      </div>
    );
  }
  
  return (
    <ResponsiveContainer width="100%" height={130}>
      <LineChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
        <XAxis dataKey="mese" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
        <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" />
        <Tooltip 
          contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px', fontSize: '12px' }}
          labelStyle={{ color: '#9CA3AF' }}
        />
        <Line 
          type="monotone" 
          dataKey="partite" 
          stroke={CHART_COLORS.primary} 
          strokeWidth={2}
          dot={{ fill: CHART_COLORS.primary, strokeWidth: 0, r: 3 }}
          activeDot={{ r: 5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

const MiniBarChart = ({ data, dataKey, label, color }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-32 flex items-center justify-center text-gray-400 text-sm">
        Nessun dato disponibile
      </div>
    );
  }
  
  return (
    <ResponsiveContainer width="100%" height={130}>
      <BarChart data={data} layout="vertical" margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
        <XAxis type="number" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
        <YAxis type="category" dataKey="nome" tick={{ fontSize: 10 }} stroke="#9CA3AF" width={60} />
        <Tooltip 
          contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px', fontSize: '12px' }}
          formatter={(value) => [`${value} ${label}`, '']}
        />
        <Bar dataKey={dataKey} radius={[0, 4, 4, 0]}>
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={CHART_COLORS.bars[index] || CHART_COLORS.primary} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const DashboardCharts = ({ token }) => {
  const [chartsData, setChartsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCharts();
  }, []);

  const loadCharts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/club-stats/v2/dashboard-charts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setChartsData(data.charts);
        }
      }
    } catch (e) {
      console.error('Error loading charts:', e);
    }
    setLoading(false);
  };

  // Non mostrare nulla se non ci sono dati
  const hasData = chartsData && (
    chartsData.trend_partite?.some(d => d.partite > 0) ||
    chartsData.top_marcatori?.length > 0 ||
    chartsData.top_ranking?.length > 0
  );

  if (loading) {
    return null; // Non mostra loader per non essere invasivo
  }

  if (!hasData) {
    return null; // Non mostra sezione se non ci sono dati
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <span>📊</span> Statistiche Rapide
        </h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Trend Partite Mensili */}
        {chartsData.trend_partite?.some(d => d.partite > 0) && (
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-1">
              <span>📈</span> Partite Mensili
            </h4>
            <MiniTrendChart data={chartsData.trend_partite} />
          </div>
        )}
        
        {/* Top 5 Marcatori */}
        {chartsData.top_marcatori?.length > 0 && (
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-1">
              <span>⚽</span> Top Marcatori
            </h4>
            <MiniBarChart data={chartsData.top_marcatori} dataKey="gol" label="gol" color={CHART_COLORS.success} />
          </div>
        )}
        
        {/* Top 5 Ranking */}
        {chartsData.top_ranking?.length > 0 && (
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-1">
              <span>🏆</span> Top Ranking
            </h4>
            <MiniBarChart data={chartsData.top_ranking} dataKey="punti" label="pts" color={CHART_COLORS.secondary} />
          </div>
        )}
      </div>
    </div>
  );
};


// ============================================
// 📅 CARD EVENTO PER BACHECA - CON LOCANDINA GRANDE
// ============================================

const EventoCardBacheca = ({ annuncio, onEdit, onDelete, clubId, userNome }) => {
  const [showIscrizioneForm, setShowIscrizioneForm] = useState(false);
  const [iscrizioneLoading, setIscrizioneLoading] = useState(false);
  const [iscrizioneMsg, setIscrizioneMsg] = useState(null);
  const [squadra, setSquadra] = useState('');
  const [iscritti, setIscritti] = useState(null);
  const [loadingIscritti, setLoadingIscritti] = useState(false);

  // Carica iscritti
  useEffect(() => {
    if (annuncio.evento_id) {
      loadIscritti();
    }
  }, [annuncio.evento_id]);

  const loadIscritti = async () => {
    setLoadingIscritti(true);
    try {
      const token = localStorage.getItem('club_token');
      const res = await fetch(`${BACKEND_URL}/api/clubs/calendario/eventi/${annuncio.evento_id}/iscrizioni`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIscritti(data.iscrizioni || []);
      }
    } catch (e) {
      console.error('Errore caricamento iscritti', e);
    }
    setLoadingIscritti(false);
  };

  // Iscrizione
  const handleIscrizione = async (e) => {
    e.preventDefault();
    if (!squadra.trim()) {
      setIscrizioneMsg({ type: 'error', text: 'Inserisci la squadra' });
      return;
    }
    
    setIscrizioneLoading(true);
    try {
      const token = localStorage.getItem('club_token');
      const res = await fetch(`${BACKEND_URL}/api/clubs/calendario/eventi/${annuncio.evento_id}/iscrizioni`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          partecipante_nome: userNome,
          squadra: squadra.trim(),
          tipo_partecipante: 'socio'
        })
      });
      
      if (res.ok) {
        setIscrizioneMsg({ type: 'success', text: 'Iscrizione completata!' });
        setShowIscrizioneForm(false);
        setSquadra('');
        loadIscritti();
      } else {
        const err = await res.json();
        setIscrizioneMsg({ type: 'error', text: err.detail || 'Errore iscrizione' });
      }
    } catch (e) {
      setIscrizioneMsg({ type: 'error', text: 'Errore di rete' });
    }
    setIscrizioneLoading(false);
    setTimeout(() => setIscrizioneMsg(null), 3000);
  };

  // Formatta data
  const formatData = (dataStr) => {
    if (!dataStr) return '';
    try {
      const d = new Date(dataStr);
      return d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return dataStr;
    }
  };

  // Controlla se l'utente è già iscritto
  const isGiaIscritto = iscritti?.some(i => i.partecipante_nome === userNome);

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-yellow-400">
      {/* LOCANDINA GRANDE */}
      {annuncio.evento_copertina ? (
        <div className="w-full">
          <img 
            src={annuncio.evento_copertina} 
            alt={annuncio.title}
            className="w-full h-auto max-h-[400px] object-cover"
          />
        </div>
      ) : (
        <div className="w-full h-48 bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
          <span className="text-8xl">📅</span>
        </div>
      )}

      {/* CONTENUTO */}
      <div className="p-5">
        {/* Badge */}
        <div className="flex items-center gap-2 mb-2">
          <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded font-bold">EVENTO</span>
          {annuncio.is_pinned && <span className="text-sm">📌</span>}
        </div>

        {/* Titolo */}
        <h3 className="text-2xl font-bold text-gray-800 mb-3">{annuncio.title}</h3>

        {/* Info evento */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-gray-600">
            <span className="text-lg">📆</span>
            <span className="font-medium">{formatData(annuncio.evento_data)}</span>
            {annuncio.evento_ora && <span>• {annuncio.evento_ora}</span>}
          </div>
          {annuncio.evento_luogo && (
            <div className="flex items-center gap-2 text-gray-600">
              <span className="text-lg">📍</span>
              <span>{annuncio.evento_luogo}</span>
            </div>
          )}
          {annuncio.evento_max_partecipanti && (
            <div className="flex items-center gap-2 text-gray-600">
              <span className="text-lg">👥</span>
              <span>Max {annuncio.evento_max_partecipanti} partecipanti</span>
              {iscritti && <span className="text-green-600 font-medium">({iscritti.length} iscritti)</span>}
            </div>
          )}
        </div>

        {/* Descrizione */}
        {annuncio.content && (
          <p className="text-gray-600 mb-4 whitespace-pre-wrap">{annuncio.content}</p>
        )}

        {/* Messaggio iscrizione */}
        {iscrizioneMsg && (
          <div className={`p-3 rounded-lg mb-4 ${iscrizioneMsg.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {iscrizioneMsg.text}
          </div>
        )}

        {/* PULSANTE ISCRIVITI o stato */}
        <div className="space-y-3">
          {isGiaIscritto ? (
            <div className="bg-green-100 text-green-700 p-4 rounded-xl text-center">
              <span className="text-2xl">✅</span>
              <p className="font-bold">Sei già iscritto!</p>
            </div>
          ) : showIscrizioneForm ? (
            <form onSubmit={handleIscrizione} className="bg-blue-50 p-4 rounded-xl space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Tuo nome</label>
                <input 
                  type="text" 
                  value={userNome} 
                  disabled 
                  className="w-full border rounded-lg px-3 py-2 bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Squadra *</label>
                <input 
                  type="text" 
                  value={squadra}
                  onChange={(e) => setSquadra(e.target.value)}
                  placeholder="Es: Real Madrid, Bayern Monaco..."
                  className="w-full border rounded-lg px-3 py-2"
                  required
                />
              </div>
              <div className="flex gap-2">
                <button 
                  type="submit"
                  disabled={iscrizioneLoading}
                  className="flex-1 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 disabled:bg-gray-400"
                >
                  {iscrizioneLoading ? 'Iscrizione...' : '✅ Conferma Iscrizione'}
                </button>
                <button 
                  type="button"
                  onClick={() => setShowIscrizioneForm(false)}
                  className="px-4 py-3 bg-gray-200 rounded-xl hover:bg-gray-300"
                >
                  Annulla
                </button>
              </div>
            </form>
          ) : (
            <button 
              onClick={() => setShowIscrizioneForm(true)}
              className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold text-lg hover:from-green-600 hover:to-green-700 shadow-lg transition-all transform hover:scale-[1.02]"
              data-testid="btn-iscriviti-evento"
            >
              🎯 ISCRIVITI ORA
            </button>
          )}
        </div>

        {/* Lista iscritti */}
        {iscritti && iscritti.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <h4 className="font-medium text-sm text-gray-500 mb-2">👥 Iscritti ({iscritti.length})</h4>
            <div className="flex flex-wrap gap-2">
              {iscritti.slice(0, 10).map(i => (
                <span key={i.id} className="bg-gray-100 px-2 py-1 rounded text-xs">
                  {i.partecipante_nome}
                </span>
              ))}
              {iscritti.length > 10 && (
                <span className="text-xs text-gray-400">+{iscritti.length - 10} altri</span>
              )}
            </div>
          </div>
        )}

        {/* Footer con admin actions */}
        <div className="flex justify-between items-center mt-4 pt-4 border-t">
          <p className="text-xs text-gray-400">
            {annuncio.author} • {new Date(annuncio.created_at).toLocaleDateString('it-IT')}
          </p>
          <div className="flex gap-1">
            <button onClick={onEdit} className="p-2 text-blue-600 hover:bg-blue-50 rounded" title="Modifica">
              ✏️
            </button>
            <button onClick={onDelete} className="p-2 text-red-600 hover:bg-red-50 rounded" title="Elimina">
              🗑️
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 🏢 CLUB AUTH CONTEXT
export const ClubAuthContext = createContext();

export const ClubAuthProvider = ({ children }) => {
  const [clubAuthState, setClubAuthState] = useState({
    token: localStorage.getItem('club_token'),
    club: null,
    isAuthenticated: false,
    isLoading: true
  });

  useEffect(() => {
    const clubToken = localStorage.getItem('club_token');
    if (clubToken) verifyClubToken(clubToken);
    else setClubAuthState(prev => ({ ...prev, isLoading: false }));
  }, []);

  const verifyClubToken = async (token) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/clubs/auth/me`, { headers: { 'Authorization': `Bearer ${token}` }});
      if (res.ok) {
        const data = await res.json();
        setClubAuthState({ token, club: data.club, isAuthenticated: true, isLoading: false });
      } else {
        localStorage.removeItem('club_token');
        setClubAuthState({ token: null, club: null, isAuthenticated: false, isLoading: false });
      }
    } catch { setClubAuthState({ token: null, club: null, isAuthenticated: false, isLoading: false }); }
  };

  const loginClub = async (username, password) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/clubs/auth/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username, password }) });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('club_token', data.access_token);
        setClubAuthState({ token: data.access_token, club: data.club, isAuthenticated: true, isLoading: false });
        return { success: true };
      }
      return { success: false, error: data.detail || 'Errore' };
    } catch { return { success: false, error: 'Errore di connessione' }; }
  };

  const registerClub = async (clubData) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/clubs/auth/register`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(clubData) });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('club_token', data.access_token);
        setClubAuthState({ token: data.access_token, club: data.club, isAuthenticated: true, isLoading: false });
        return { success: true };
      }
      return { success: false, error: data.detail || 'Errore' };
    } catch { return { success: false, error: 'Errore di connessione' }; }
  };

  const logoutClub = () => {
    localStorage.removeItem('club_token');
    setClubAuthState({ token: null, club: null, isAuthenticated: false, isLoading: false });
  };

  const refreshClubData = () => {
    const token = localStorage.getItem('club_token');
    if (token) verifyClubToken(token);
  };

  return (
    <ClubAuthContext.Provider value={{ ...clubAuthState, loginClub, registerClub, logoutClub, refreshClubData }}>
      {children}
    </ClubAuthContext.Provider>
  );
};

// 🏢 CLUB LOGIN
export const ClubLoginForm = () => {
  const navigate = useNavigate();
  const { loginClub, isAuthenticated } = useContext(ClubAuthContext);
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (isAuthenticated) navigate('/club/dashboard'); }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await loginClub(form.username, form.password);
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_subbuteo-saas/artifacts/knve52ma_sfondo%20app.jpg')", backgroundSize: "cover" }}>
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <span className="text-5xl">🏢</span>
          <h2 className="text-2xl font-bold mt-4">Accesso Club</h2>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input type="text" placeholder="Username club" value={form.username} onChange={e => setForm({...form, username: e.target.value})}
            className="w-full px-4 py-2 border rounded-lg" required data-testid="club-login-username" />
          <input type="password" placeholder="Password" value={form.password} onChange={e => setForm({...form, password: e.target.value})}
            className="w-full px-4 py-2 border rounded-lg" required data-testid="club-login-password" />
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <button type="submit" disabled={loading} className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400" data-testid="club-login-submit">
            {loading ? 'Accesso...' : 'Accedi'}
          </button>
        </form>
        <div className="mt-4 text-center text-sm">
          <button onClick={() => navigate('/registra-club')} className="text-blue-600">Registra nuovo club</button>
          <span className="mx-2">|</span>
          <button onClick={() => navigate('/login')} className="text-green-600">Accesso utente</button>
        </div>
      </div>
    </div>
  );
};

// 🏢 CLUB REGISTER
export const ClubRegisterForm = () => {
  const navigate = useNavigate();
  const { registerClub, isAuthenticated } = useContext(ClubAuthContext);
  const [form, setForm] = useState({ club_name: '', username: '', email: '', password: '', confirmPassword: '', club_city: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (isAuthenticated) navigate('/club/dashboard'); }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) { setError('Le password non coincidono'); return; }
    if (!/^[a-z0-9_]+$/.test(form.username)) { setError('Username: solo minuscole, numeri e _'); return; }
    setLoading(true);
    const result = await registerClub(form);
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_subbuteo-saas/artifacts/knve52ma_sfondo%20app.jpg')", backgroundSize: "cover" }}>
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <span className="text-5xl">🏢</span>
          <h2 className="text-2xl font-bold mt-4">Registra Club</h2>
          <p className="text-sm text-blue-600 mt-2">🎉 30 giorni di prova gratuita!</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input type="text" placeholder="Nome Club *" value={form.club_name} onChange={e => setForm({...form, club_name: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required data-testid="club-register-name" />
          <input type="text" placeholder="Username *" value={form.username} onChange={e => setForm({...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')})} className="w-full px-4 py-2 border rounded-lg" required data-testid="club-register-username" />
          <input type="email" placeholder="Email *" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required data-testid="club-register-email" />
          <input type="text" placeholder="Città" value={form.club_city} onChange={e => setForm({...form, club_city: e.target.value})} className="w-full px-4 py-2 border rounded-lg" data-testid="club-register-city" />
          <input type="password" placeholder="Password *" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required data-testid="club-register-password" />
          <input type="password" placeholder="Conferma Password *" value={form.confirmPassword} onChange={e => setForm({...form, confirmPassword: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required data-testid="club-register-confirm-password" />
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <button type="submit" disabled={loading} className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400" data-testid="club-register-submit">
            {loading ? 'Registrazione...' : 'Registra Club'}
          </button>
        </form>
        <div className="mt-4 text-center text-sm">
          <button onClick={() => navigate('/login-club')} className="text-blue-600">Accedi al club</button>
        </div>
      </div>
    </div>
  );
};

// 🏢 CLUB DASHBOARD
export const ClubDashboard = () => {
  const navigate = useNavigate();
  const { club, token, isAuthenticated, isLoading, logoutClub, refreshClubData } = useContext(ClubAuthContext);
  const { showConfirm } = useConfirmDialog();
  
  const [activeTab, setActiveTab] = useState('home');
  const [codes, setCodes] = useState([]);
  const [members, setMembers] = useState([]);
  const [anagrafica, setAnagrafica] = useState([]);
  const [anagraficaStats, setAnagraficaStats] = useState({});
  const [stats, setStats] = useState({ members: {}, codes: {}, subscription: {} });
  
  // 💳 PREMIUM UPGRADE STATES
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [loadingPayment, setLoadingPayment] = useState(false);
  
  // 📥 IMPORT EXCEL STATES
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importStep, setImportStep] = useState('upload'); // upload, preview, execute, result
  const [importProgress, setImportProgress] = useState({ show: false, message: '', percent: 0 });
  const [importResult, setImportResult] = useState(null);
  const [replaceExistingData, setReplaceExistingData] = useState(false);
  const [customColumnMapping, setCustomColumnMapping] = useState({}); // Editor visuale
  const [showMappingEditor, setShowMappingEditor] = useState(false);
  
  // 🏆 TORNEI V1 STATES (legacy)
  const [tornei, setTornei] = useState([]);
  const [torneoSelezionato, setTorneoSelezionato] = useState(null);
  const [showNuovoTorneo, setShowNuovoTorneo] = useState(false);
  const [torneoForm, setTorneoForm] = useState({
    nome: '', formato: 'girone_singolo', luogo: '', descrizione: '',
    data_inizio: '', data_fine: '',
    punti_vittoria: 3, punti_pareggio: 1, punti_sconfitta: 0,
    bonus_presenza: 1, bonus_vittoria_singola: 0,
    punti_ranking: { "1": 25, "2": 20, "3": 15, "4": 12, "5": 10, "6": 8, "7": 6, "8": 4 }
  });
  const [loadingTornei, setLoadingTornei] = useState(false);
  const [showAddPartecipante, setShowAddPartecipante] = useState(false);
  const [partecipanteForm, setPartecipanteForm] = useState({ tipo: 'socio', socio_id: '', nome_ospite: '' });
  const [showAddPartita, setShowAddPartita] = useState(false);
  const [partitaForm, setPartitaForm] = useState({ partecipante_a_id: '', partecipante_b_id: '', gol_a: 0, gol_b: 0, fase: 'girone' });
  const [editingPartitaId, setEditingPartitaId] = useState(null); // ID partita in modifica (B1 fix)
  
  // 🏆 TORNEI V2 STATES (nuovo sistema avanzato)
  const [torneoV2Selezionato, setTorneoV2Selezionato] = useState(null);
  
  // 🏆 TORNEI V3 STATES
  const [torneoV3Selezionato, setTorneoV3Selezionato] = useState(null);
  const [importedTournamentId, setImportedTournamentId] = useState(null);
  
  // 📊 STATISTICHE V2 STATES
  const [showSchedaGiocatore, setShowSchedaGiocatore] = useState(null); // {nome, socioId}
  
  // Forms
  const [codeType, setCodeType] = useState('socio');
  const [codeQty, setCodeQty] = useState(1);
  const [newCodes, setNewCodes] = useState([]);
  const [generating, setGenerating] = useState(false);
  
  // Add/Edit Socio Form
  const [showAddSocio, setShowAddSocio] = useState(false);
  const [editingSocio, setEditingSocio] = useState(null); // null = add, object = edit
  const [socioForm, setSocioForm] = useState({ nome: '', cognome: '', nickname: '', email: '', telefono: '', ruolo: 'socio', numero_tessera: '', note: '' });
  const [addingSocio, setAddingSocio] = useState(false);
  const [newSocioCode, setNewSocioCode] = useState('');
  
  // 📊 STATISTICHE CLUB
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loadingDashboardStats, setLoadingDashboardStats] = useState(false);
  const [activeClassifica, setActiveClassifica] = useState('stagione');
  const [expandedSocio, setExpandedSocio] = useState(null);
  
  // 🏠 DASHBOARD WIDGETS (F20-F22)
  const [dashboardWidgets, setDashboardWidgets] = useState(null);
  const [loadingWidgets, setLoadingWidgets] = useState(false);
  
  // 🌍 TORNEI ESTERNI
  const [torneiEsterni, setTorneiEsterni] = useState([]);
  const [loadingTorneiEsterni, setLoadingTorneiEsterni] = useState(false);
  const [showAddTorneoEsterno, setShowAddTorneoEsterno] = useState(false);
  const [torneoEsternoForm, setTorneoEsternoForm] = useState({
    nome: '', data_torneo: '', data_iscrizioni: '', link: '', note: ''
  });
  const [savingTorneoEsterno, setSavingTorneoEsterno] = useState(false);
  const [togglingPartecipazione, setTogglingPartecipazione] = useState(null); // torneo_id in corso di toggle
  
  // Settings
  const [editSettings, setEditSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ club_name: '', club_city: '', club_description: '' });
  const [savingSettings, setSavingSettings] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [togglingClubPublic, setTogglingClubPublic] = useState(false);
  
  // 🎨 PERSONALIZZAZIONE CLUB
  const [clubCustomization, setClubCustomization] = useState(null);
  
  // 📢 BACHECA & FEED
  const [announcements, setAnnouncements] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [showNewAnnouncement, setShowNewAnnouncement] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({ title: '', content: '', priority: 'normal', is_pinned: false, attachments: [], send_email: false });
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  
  // Mobile sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Messages
  const [msg, setMsg] = useState({ type: '', text: '' });

  useEffect(() => { if (!isLoading && !isAuthenticated) navigate('/login-club'); }, [isAuthenticated, isLoading, navigate]);

  // 💳 Handle PayPal return URL for club
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentId = urlParams.get('paymentId');
    const PayerID = urlParams.get('PayerID');
    
    if (paymentId && PayerID && token) {
      handlePayPalClubReturn(paymentId, PayerID);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [token]);

  const handlePayPalClubReturn = async (paymentId, PayerID) => {
    setLoadingPayment(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/club-subscription/execute-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ paymentId, PayerID })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          showMsg('success', '🎉 Abbonamento Premium attivato con successo!');
          loadAll(); // Refresh subscription status
        } else {
          showMsg('error', 'Errore nell\'attivazione dell\'abbonamento');
        }
      } else {
        showMsg('error', 'Errore nell\'esecuzione del pagamento');
      }
    } catch (error) {
      console.error('PayPal execute error:', error);
      showMsg('error', 'Errore durante l\'esecuzione del pagamento');
    }
    setLoadingPayment(false);
    setShowUpgradeModal(false);
  };

  useEffect(() => {
    if (token && isAuthenticated) {
      loadAll();
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    if (club) setSettingsForm({ club_name: club.name || '', club_city: club.city || '', club_description: club.description || '' });
  }, [club]);

  const api = async (url, opts = {}) => {
    try {
      // Handle FormData (no Content-Type header needed)
      const headers = { 'Authorization': `Bearer ${token}` };
      if (!opts.isFormData) {
        headers['Content-Type'] = 'application/json';
      }
      
      const fetchOpts = { 
        ...opts, 
        headers: { ...headers, ...opts.headers }
      };
      delete fetchOpts.isFormData; // Remove custom option
      
      const res = await fetch(`${BACKEND_URL}${url}`, fetchOpts);
      return res;
    } catch { return null; }
  };

  const loadAll = () => { loadCodes(); loadMembers(); loadStats(); loadAnagrafica(); loadTornei(); loadDashboardStats(); loadDashboardWidgets(); loadVisibility(); loadAnnouncements(); loadActivityFeed(); loadTorneiEsterni(); loadClubCustomization(); };

  const loadCodes = async () => { const r = await api('/api/clubs/codes'); if (r?.ok) { const d = await r.json(); setCodes(d.codes || []); } };
  const loadMembers = async () => { const r = await api('/api/clubs/members'); if (r?.ok) { const d = await r.json(); setMembers(d.members || []); } };
  const loadStats = async () => { const r = await api('/api/clubs/stats'); if (r?.ok) { const d = await r.json(); setStats(d); } };
  const loadAnagrafica = async () => { const r = await api('/api/clubs/anagrafica'); if (r?.ok) { const d = await r.json(); setAnagrafica(d.soci || []); setAnagraficaStats(d.stats || {}); } };
  
  // 🎨 PERSONALIZZAZIONE CLUB
  const loadClubCustomization = async () => {
    try {
      const r = await api('/api/clubs/customization');
      if (r?.ok) {
        const d = await r.json();
        setClubCustomization(d);
      }
    } catch (e) {
      console.error('Errore caricamento personalizzazione:', e);
    }
  };
  
  // 🌍 TORNEI ESTERNI FUNCTIONS
  const loadTorneiEsterni = async () => {
    setLoadingTorneiEsterni(true);
    try {
      const r = await api('/api/clubs/tornei-esterni');
      if (r?.ok) {
        const d = await r.json();
        setTorneiEsterni(d.tornei || []);
      }
    } catch (e) {
      console.error('Errore caricamento tornei esterni:', e);
    }
    setLoadingTorneiEsterni(false);
  };
  
  const handleAddTorneoEsterno = async () => {
    if (!torneoEsternoForm.nome || !torneoEsternoForm.data_torneo || !torneoEsternoForm.link) {
      showMsg('error', 'Compila nome, data torneo e link');
      return;
    }
    setSavingTorneoEsterno(true);
    try {
      const r = await api('/api/clubs/tornei-esterni', {
        method: 'POST',
        body: JSON.stringify(torneoEsternoForm)
      });
      if (r?.ok) {
        showMsg('success', 'Torneo esterno aggiunto!');
        setShowAddTorneoEsterno(false);
        setTorneoEsternoForm({ nome: '', data_torneo: '', data_iscrizioni: '', link: '', note: '' });
        loadTorneiEsterni();
      } else {
        showMsg('error', 'Errore nell\'aggiunta del torneo');
      }
    } catch (e) {
      showMsg('error', 'Errore nell\'aggiunta del torneo');
    }
    setSavingTorneoEsterno(false);
  };
  
  const handleDeleteTorneoEsterno = async (id) => {
    if (!window.confirm('Eliminare questo torneo esterno?')) return;
    try {
      const r = await api(`/api/clubs/tornei-esterni/${id}`, { method: 'DELETE' });
      if (r?.ok) {
        showMsg('success', 'Torneo eliminato');
        loadTorneiEsterni();
      }
    } catch (e) {
      showMsg('error', 'Errore nell\'eliminazione');
    }
  };
  
  // 🎯 TOGGLE "IO PARTECIPO" - TORNEI ESTERNI
  const handleTogglePartecipazione = async (torneoId) => {
    setTogglingPartecipazione(torneoId);
    try {
      const r = await api(`/api/clubs/tornei-esterni/${torneoId}/partecipa`, { method: 'POST' });
      if (r?.ok) {
        const d = await r.json();
        showMsg('success', d.message);
        loadTorneiEsterni(); // Refresh lista tornei
      } else {
        showMsg('error', 'Errore nella partecipazione');
      }
    } catch (e) {
      showMsg('error', 'Errore nella partecipazione');
    }
    setTogglingPartecipazione(null);
  };
  
  // Check se l'utente corrente partecipa al torneo
  const isUserPartecipante = (torneo) => {
    if (!torneo.partecipanti || !club) return false;
    const userId = club.id;
    const username = club.username;
    return torneo.partecipanti.some(
      p => p.socio_id === userId || (p.nome && p.nome.toLowerCase() === username?.toLowerCase())
    );
  };
  
  // 🏠 DASHBOARD WIDGETS (F20-F22)
  const loadDashboardWidgets = async () => {
    setLoadingWidgets(true);
    try {
      const r = await api('/api/club-stats/v2/dashboard-widgets');
      if (r?.ok) { 
        const d = await r.json(); 
        setDashboardWidgets(d.widgets || null); 
      }
    } catch (e) {
      console.error('Errore caricamento widgets:', e);
    }
    setLoadingWidgets(false);
  };
  
  // 💳 PREMIUM PAYMENT FUNCTIONS
  const handleClubPayment = async () => {
    setLoadingPayment(true);
    try {
      const response = await api('/api/club-subscription/create-payment', { method: 'POST' });
      if (response?.ok) {
        const data = await response.json();
        if (data.approval_url) {
          // Redirect to PayPal
          window.location.href = data.approval_url;
        } else {
          showMsg('error', 'Errore nella creazione del pagamento');
        }
      } else {
        showMsg('error', 'Errore nella creazione del pagamento');
      }
    } catch (error) {
      console.error('Payment error:', error);
      showMsg('error', 'Errore durante il pagamento');
    }
    setLoadingPayment(false);
  };
  
  // 📢 BACHECA FUNCTIONS
  const loadAnnouncements = async () => {
    const r = await api('/api/clubs/announcements');
    if (r?.ok) { const d = await r.json(); setAnnouncements(d.announcements || []); }
  };
  
  const loadActivityFeed = async () => {
    const r = await api('/api/clubs/activity-feed');
    if (r?.ok) { const d = await r.json(); setActivityFeed(d.activities || []); }
  };
  
  const createAnnouncement = async () => {
    if (!announcementForm.title || !announcementForm.content) {
      showMsg('error', 'Titolo e contenuto obbligatori');
      return;
    }
    setSavingAnnouncement(true);
    const r = await api('/api/clubs/announcements', { method: 'POST', body: JSON.stringify(announcementForm) });
    if (r?.ok) {
      showMsg('success', 'Annuncio pubblicato!');
      setShowNewAnnouncement(false);
      setAnnouncementForm({ title: '', content: '', priority: 'normal', is_pinned: false, send_email: false });
      loadAnnouncements();
      loadActivityFeed();
    } else {
      showMsg('error', 'Errore pubblicazione annuncio');
    }
    setSavingAnnouncement(false);
  };
  
  const updateAnnouncement = async () => {
    if (!editingAnnouncement) return;
    setSavingAnnouncement(true);
    const r = await api(`/api/clubs/announcements/${editingAnnouncement.id}`, { 
      method: 'PUT', 
      body: JSON.stringify(announcementForm) 
    });
    if (r?.ok) {
      showMsg('success', 'Annuncio aggiornato!');
      setEditingAnnouncement(null);
      setAnnouncementForm({ title: '', content: '', priority: 'normal', is_pinned: false, attachments: [], send_email: false });
      loadAnnouncements();
    } else {
      showMsg('error', 'Errore aggiornamento');
    }
    setSavingAnnouncement(false);
  };
  
  const deleteAnnouncement = async (id) => {
    showConfirm({
      title: 'Elimina Annuncio',
      message: 'Sei sicuro di voler eliminare questo annuncio?',
      confirmText: '🗑️ Elimina',
      cancelText: 'Annulla',
      variant: 'danger',
      onConfirm: async () => {
        const r = await api(`/api/clubs/announcements/${id}`, { method: 'DELETE' });
        if (r?.ok) {
          showMsg('success', 'Annuncio eliminato');
          loadAnnouncements();
        }
      }
    });
  };
  
  // 📎 UPLOAD ALLEGATI
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Verifica limite file
    const currentCount = announcementForm.attachments?.length || 0;
    if (currentCount + files.length > 3) {
      showMsg('error', 'Massimo 3 allegati per annuncio');
      return;
    }
    
    // Verifica dimensione
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        showMsg('error', `File "${file.name}" troppo grande (max 5MB)`);
        return;
      }
    }
    
    setUploadingFiles(true);
    
    try {
      const uploadedFiles = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await fetch(`${BACKEND_URL}/api/clubs/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (res.ok) {
          const data = await res.json();
          uploadedFiles.push(data.file);
        } else {
          showMsg('error', `Errore upload ${file.name}`);
        }
      }
      
      setAnnouncementForm(prev => ({
        ...prev,
        attachments: [...(prev.attachments || []), ...uploadedFiles]
      }));
      
      if (uploadedFiles.length > 0) {
        showMsg('success', `${uploadedFiles.length} file caricati`);
      }
    } catch (err) {
      showMsg('error', 'Errore upload file');
    }
    
    setUploadingFiles(false);
    e.target.value = ''; // Reset input
  };
  
  const removeAttachment = (index) => {
    setAnnouncementForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };
  
  const deleteActivity = async (id) => {
    const r = await api(`/api/clubs/activity-feed/${id}`, { method: 'DELETE' });
    if (r?.ok) loadActivityFeed();
  };
  
  // 🌐 VISIBILITÀ CLUB
  const loadVisibility = async () => {
    const r = await api('/api/clubs/visibility');
    if (r?.ok) { const d = await r.json(); setIsPublic(d.is_public || false); }
  };
  
  const toggleVisibility = async () => {
    setSavingVisibility(true);
    const r = await api('/api/clubs/visibility', { method: 'PUT', body: JSON.stringify({ is_public: !isPublic }) });
    if (r?.ok) {
      setIsPublic(!isPublic);
      showMsg('success', !isPublic ? 'Club ora pubblico!' : 'Club ora privato');
    } else {
      showMsg('error', 'Errore aggiornamento visibilità');
    }
    setSavingVisibility(false);
  };
  
  // 📊 STATISTICHE DASHBOARD
  const loadDashboardStats = async () => {
    setLoadingDashboardStats(true);
    const r = await api('/api/clubs/dashboard-stats');
    if (r?.ok) { const d = await r.json(); setDashboardStats(d); }
    setLoadingDashboardStats(false);
  };
  
  // 🏆 TORNEI FUNCTIONS
  const loadTornei = async () => {
    setLoadingTornei(true);
    const r = await api('/api/clubs/tornei');
    if (r?.ok) { const d = await r.json(); setTornei(d.tornei || []); }
    setLoadingTornei(false);
  };

  const createTorneo = async () => {
    if (!torneoForm.nome) { showMsg('error', 'Nome torneo obbligatorio'); return; }
    const r = await api('/api/clubs/tornei', { method: 'POST', body: JSON.stringify(torneoForm) });
    if (r?.ok) {
      const d = await r.json();
      showMsg('success', 'Torneo creato!');
      setShowNuovoTorneo(false);
      setTorneoForm({ nome: '', formato: 'girone_singolo', luogo: '', descrizione: '', data_inizio: '', data_fine: '', punti_vittoria: 3, punti_pareggio: 1, punti_sconfitta: 0, bonus_presenza: 1, bonus_vittoria_singola: 0, punti_ranking: { "1": 25, "2": 20, "3": 15, "4": 12, "5": 10, "6": 8, "7": 6, "8": 4 } });
      loadTornei();
      setTorneoSelezionato(d.torneo);
    } else { showMsg('error', 'Errore creazione torneo'); }
  };

  const loadTorneoDettaglio = async (id) => {
    const r = await api(`/api/clubs/tornei/${id}`);
    if (r?.ok) { const d = await r.json(); setTorneoSelezionato(d.torneo); }
  };

  const addPartecipante = async () => {
    if (!torneoSelezionato) return;
    const body = partecipanteForm.tipo === 'socio' 
      ? { tipo: 'socio', socio_id: partecipanteForm.socio_id }
      : { tipo: 'ospite', nome_ospite: partecipanteForm.nome_ospite };
    const r = await api(`/api/clubs/tornei/${torneoSelezionato.id}/partecipanti`, { method: 'POST', body: JSON.stringify(body) });
    if (r?.ok) {
      showMsg('success', 'Partecipante aggiunto');
      loadTorneoDettaglio(torneoSelezionato.id);
      setShowAddPartecipante(false);
      setPartecipanteForm({ tipo: 'socio', socio_id: '', nome_ospite: '' });
    } else { const d = await r?.json(); showMsg('error', d?.detail || 'Errore'); }
  };

  const removePartecipante = async (partId, nome) => {
    if (!torneoSelezionato) return;
    showConfirm({
      title: 'Rimuovi Partecipante',
      message: `Vuoi rimuovere ${nome || 'questo partecipante'} dal torneo?`,
      confirmText: '🗑️ Rimuovi',
      cancelText: 'Annulla',
      variant: 'warning',
      onConfirm: async () => {
        const r = await api(`/api/clubs/tornei/${torneoSelezionato.id}/partecipanti/${partId}`, { method: 'DELETE' });
        if (r?.ok) { showMsg('success', 'Rimosso'); loadTorneoDettaglio(torneoSelezionato.id); }
      }
    });
  };

  const addPartita = async () => {
    if (!torneoSelezionato) return;
    if (partitaForm.partecipante_a_id === partitaForm.partecipante_b_id) { showMsg('error', 'Seleziona due partecipanti diversi'); return; }
    
    // B1 FIX: Se stiamo modificando una partita esistente, usa PUT invece di POST
    const isEditing = editingPartitaId !== null;
    const url = isEditing 
      ? `/api/clubs/tornei/${torneoSelezionato.id}/partite/${editingPartitaId}`
      : `/api/clubs/tornei/${torneoSelezionato.id}/partite`;
    const method = isEditing ? 'PUT' : 'POST';
    
    const r = await api(url, { method, body: JSON.stringify(partitaForm) });
    if (r?.ok) {
      showMsg('success', isEditing ? 'Risultato aggiornato' : 'Partita aggiunta');
      loadTorneoDettaglio(torneoSelezionato.id);
      setShowAddPartita(false);
      setEditingPartitaId(null); // Reset editing state
      setPartitaForm({ partecipante_a_id: '', partecipante_b_id: '', gol_a: 0, gol_b: 0, fase: 'girone' });
    } else { const d = await r?.json(); showMsg('error', d?.detail || 'Errore'); }
  };

  // B1 FIX: Funzione per aprire modifica partita esistente
  const editPartita = (partita) => {
    if (!torneoSelezionato) return;
    // Verifica che il torneo non sia completato
    if (torneoSelezionato.status === 'completato') {
      showMsg('error', 'Impossibile modificare: torneo già terminato');
      return;
    }
    setEditingPartitaId(partita.id);
    setPartitaForm({
      partecipante_a_id: partita.partecipante_a_id,
      partecipante_b_id: partita.partecipante_b_id,
      gol_a: partita.gol_a,
      gol_b: partita.gol_b,
      fase: partita.fase || 'girone',
      rigori_a: partita.rigori_a || null,
      rigori_b: partita.rigori_b || null,
      serata: partita.serata || null,
      note: partita.note || ''
    });
    setShowAddPartita(true);
  };

  // B1 FIX: Reset form quando si chiude il modal
  const closePartitaModal = () => {
    setShowAddPartita(false);
    setEditingPartitaId(null);
    setPartitaForm({ partecipante_a_id: '', partecipante_b_id: '', gol_a: 0, gol_b: 0, fase: 'girone' });
  };

  const removePartita = async (partitaId) => {
    if (!torneoSelezionato) return;
    showConfirm({
      title: 'Rimuovi Partita',
      message: 'Sei sicuro di voler rimuovere questa partita?',
      confirmText: '🗑️ Rimuovi',
      cancelText: 'Annulla',
      variant: 'danger',
      onConfirm: async () => {
        const r = await api(`/api/clubs/tornei/${torneoSelezionato.id}/partite/${partitaId}`, { method: 'DELETE' });
        if (r?.ok) { showMsg('success', 'Partita rimossa'); loadTorneoDettaglio(torneoSelezionato.id); }
      }
    });
  };

  const updateTorneoStatus = async (newStatus) => {
    if (!torneoSelezionato) return;
    const r = await api(`/api/clubs/tornei/${torneoSelezionato.id}/status?new_status=${newStatus}`, { method: 'PUT' });
    if (r?.ok) { showMsg('success', `Stato: ${newStatus}`); loadTorneoDettaglio(torneoSelezionato.id); loadTornei(); }
  };

  const deleteTorneo = async (id) => {
    showConfirm({
      title: 'Elimina Torneo',
      message: 'Sei sicuro di voler eliminare questo torneo? Verranno eliminati anche tutti i risultati.',
      confirmText: '🗑️ Elimina',
      cancelText: 'Annulla',
      variant: 'danger',
      onConfirm: async () => {
        const r = await api(`/api/clubs/tornei/${id}`, { method: 'DELETE' });
        if (r?.ok) { showMsg('success', 'Torneo eliminato'); setTorneoSelezionato(null); loadTornei(); }
      }
    });
  };

  const saveFinalRanking = async () => {
    if (!torneoSelezionato) return;
    const partecipanti = torneoSelezionato.partecipanti || [];
    const classifica = partecipanti.map((p, i) => ({ posizione: i + 1, partecipante_id: p.id }));
    const r = await api(`/api/clubs/tornei/${torneoSelezionato.id}/classifica-finale`, { method: 'POST', body: JSON.stringify({ classifica }) });
    if (r?.ok) { showMsg('success', 'Classifica salvata!'); loadTorneoDettaglio(torneoSelezionato.id); loadTornei(); }
  };

  // 📧 NOTIFICHE EMAIL
  const [sendingNotifica, setSendingNotifica] = useState(false);
  
  const notificaIscrizione = async (partecipanteId) => {
    if (!torneoSelezionato) return;
    setSendingNotifica(true);
    const r = await api(`/api/clubs/tornei/${torneoSelezionato.id}/notifica-iscrizione?partecipante_id=${partecipanteId}`, { method: 'POST' });
    if (r?.ok) {
      const d = await r.json();
      showMsg(d.success ? 'success' : 'error', d.message);
    } else { showMsg('error', 'Errore invio notifica'); }
    setSendingNotifica(false);
  };

  const notificaClassifica = async () => {
    if (!torneoSelezionato) return;
    setSendingNotifica(true);
    const r = await api(`/api/clubs/tornei/${torneoSelezionato.id}/notifica-classifica`, { method: 'POST' });
    if (r?.ok) {
      const d = await r.json();
      showMsg('success', d.message);
    } else { showMsg('error', 'Errore invio notifiche'); }
    setSendingNotifica(false);
  };

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 4000); };

  const generateCodes = async () => {
    setGenerating(true);
    const r = await api('/api/clubs/codes/generate', { method: 'POST', body: JSON.stringify({ type: codeType, quantity: codeQty }) });
    if (r?.ok) { const d = await r.json(); setNewCodes(d.codes || []); loadCodes(); loadStats(); }
    else showMsg('error', 'Errore generazione');
    setGenerating(false);
  };

  const addSocio = async () => {
    if (!socioForm.nome || !socioForm.cognome) { showMsg('error', 'Nome e Cognome obbligatori'); return; }
    setAddingSocio(true);
    const r = await api('/api/clubs/anagrafica', { method: 'POST', body: JSON.stringify(socioForm) });
    if (r?.ok) {
      const d = await r.json();
      setNewSocioCode(d.socio.codice_accesso);
      showMsg('success', `Socio ${d.socio.nome_completo} aggiunto!`);
      loadAnagrafica(); loadStats();
      setSocioForm({ nome: '', cognome: '', nickname: '', email: '', telefono: '', ruolo: 'socio', numero_tessera: '', note: '' });
    } else {
      const d = await r?.json();
      showMsg('error', d?.detail || 'Errore');
    }
    setAddingSocio(false);
  };

  // 🆕 BUG-001 FIX: Funzione per modificare un socio esistente
  const startEditSocio = (socio) => {
    setEditingSocio(socio);
    setSocioForm({
      nome: socio.nome || '',
      cognome: socio.cognome || '',
      nickname: socio.nickname || '',
      email: socio.email || '',
      telefono: socio.telefono || '',
      ruolo: socio.ruolo || 'socio',
      numero_tessera: socio.numero_tessera || '',
      note: socio.note || ''
    });
    setShowAddSocio(true);
    setNewSocioCode('');
  };

  const updateSocio = async () => {
    if (!editingSocio) return;
    if (!socioForm.nome || !socioForm.cognome) { showMsg('error', 'Nome e Cognome obbligatori'); return; }
    setAddingSocio(true);
    const r = await api(`/api/clubs/anagrafica/${editingSocio.id}`, { 
      method: 'PUT', 
      body: JSON.stringify(socioForm) 
    });
    if (r?.ok) {
      showMsg('success', `Socio ${socioForm.nome} ${socioForm.cognome} aggiornato!`);
      loadAnagrafica(); loadStats();
      cancelEditSocio();
    } else {
      const d = await r?.json();
      showMsg('error', d?.detail || 'Errore nell\'aggiornamento');
    }
    setAddingSocio(false);
  };

  const cancelEditSocio = () => {
    setEditingSocio(null);
    setShowAddSocio(false);
    setNewSocioCode('');
    setSocioForm({ nome: '', cognome: '', nickname: '', email: '', telefono: '', ruolo: 'socio', numero_tessera: '', note: '' });
  };

  const removeSocio = async (id, nome) => {
    showConfirm({
      title: 'Rimuovi Socio',
      message: `Sei sicuro di voler rimuovere "${nome}" dall'anagrafica?`,
      confirmText: '🗑️ Rimuovi',
      cancelText: 'Annulla',
      variant: 'danger',
      onConfirm: async () => {
        const r = await api(`/api/clubs/anagrafica/${id}`, { method: 'DELETE' });
        if (r?.ok) { showMsg('success', 'Socio rimosso'); loadAnagrafica(); loadMembers(); loadStats(); }
      }
    });
  };

  const regenerateCode = async (id, nome, hasExistingCode = false) => {
    // Different messages for generate vs regenerate
    const title = hasExistingCode ? 'Rigenera Codice' : 'Genera Codice App';
    const message = hasExistingCode 
      ? `Vuoi generare un nuovo codice per "${nome}"? Il codice precedente non sarà più valido.`
      : `Vuoi generare un codice per "${nome}"? Il socio potrà usarlo per accedere all'app.`;
    const confirmText = hasExistingCode ? '🔄 Rigenera' : '📱 Genera Codice';
    const variant = hasExistingCode ? 'warning' : 'default';
    
    showConfirm({
      title,
      message,
      confirmText,
      cancelText: 'Annulla',
      variant,
      onConfirm: async () => {
        const r = await api(`/api/clubs/anagrafica/${id}/regenerate-code`, { method: 'POST' });
        if (r?.ok) { 
          const d = await r.json(); 
          const msg = d.is_existing 
            ? `Codice esistente: ${d.codice_accesso}` 
            : `Nuovo codice: ${d.codice_accesso}`;
          showMsg('success', msg); 
          loadAnagrafica(); 
        }
      }
    });
  };

  const updateMemberRole = async (id, role) => {
    const r = await api(`/api/clubs/members/${id}/role`, { method: 'PUT', body: JSON.stringify({ role }) });
    if (r?.ok) { showMsg('success', 'Ruolo aggiornato'); loadMembers(); loadStats(); }
  };

  const removeMember = async (id, nome) => {
    showConfirm({
      title: 'Rimuovi Membro',
      message: `Sei sicuro di voler rimuovere "${nome}" dal club?`,
      confirmText: '🗑️ Rimuovi',
      cancelText: 'Annulla',
      variant: 'danger',
      onConfirm: async () => {
        const r = await api(`/api/clubs/members/${id}`, { method: 'DELETE' });
        if (r?.ok) { showMsg('success', 'Rimosso'); loadMembers(); loadStats(); }
      }
    });
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    const r = await api('/api/clubs/settings', { method: 'PUT', body: JSON.stringify(settingsForm) });
    if (r?.ok) { showMsg('success', 'Salvato'); setEditSettings(false); refreshClubData(); }
    setSavingSettings(false);
  };

  // 🌐 TOGGLE PAGINA PUBBLICA CLUB
  const toggleClubPublic = async () => {
    setTogglingClubPublic(true);
    try {
      const r = await api('/api/clubs/toggle-public', { method: 'PUT' });
      if (r?.ok) {
        const data = await r.json();
        showMsg('success', data.message);
        refreshClubData(); // Refresh per aggiornare lo stato
      } else {
        // Estrai messaggio di errore dettagliato dalla risposta
        let errorMsg = 'Errore nell\'aggiornamento';
        try {
          const errorData = await r.json();
          errorMsg = errorData.detail || errorData.message || errorMsg;
        } catch (e) {
          errorMsg = `Errore ${r.status}: ${r.statusText || 'Problema di connessione'}`;
        }
        showMsg('error', errorMsg);
        console.error('Toggle public error:', errorMsg);
      }
    } catch (e) {
      const errorMsg = e.message || 'Errore di connessione al server';
      showMsg('error', errorMsg);
      console.error('Toggle public exception:', e);
    }
    setTogglingClubPublic(false);
  };

  // 📥 IMPORT EXCEL FUNCTIONS (BUG-005 FIX)
  const handleClubFileUpload = async (file) => {
    if (!file) return;
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!validTypes.includes(ext)) {
      showMsg('error', 'Formato non supportato. Usa CSV o Excel (.xlsx, .xls)');
      return;
    }
    
    setImportFile(file);
    setImportProgress({ show: true, message: 'Analisi file...', percent: 30 });
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('club_id', club.id);
      
      const response = await api('/api/clubs/import/preview', {
        method: 'POST',
        body: formData,
        isFormData: true
      });
      
      if (response?.ok) {
        const previewData = await response.json();
        setImportPreview(previewData);
        setImportProgress({ show: false, message: '', percent: 0 });
        setImportStep('preview');
      } else {
        const error = await response?.json();
        showMsg('error', error?.detail || 'Errore durante l\'analisi del file');
        setImportProgress({ show: false, message: '', percent: 0 });
      }
    } catch (err) {
      console.error('Import preview error:', err);
      showMsg('error', 'Errore durante l\'upload');
      setImportProgress({ show: false, message: '', percent: 0 });
    }
  };

  const executeClubImport = async () => {
    setImportStep('execute');
    setImportProgress({ show: true, message: 'Importazione in corso...', percent: 50 });
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('club_id', club.id);
      formData.append('skip_errors', 'true');
      formData.append('replace_existing', replaceExistingData.toString());
      
      // Include custom mapping if provided
      const mappingToUse = customColumnMapping && Object.keys(customColumnMapping).length > 0 
        ? customColumnMapping 
        : importPreview?.detected_columns;
      if (mappingToUse) {
        formData.append('column_mapping_json', JSON.stringify(mappingToUse));
      }
      
      const response = await api('/api/clubs/import/execute', {
        method: 'POST',
        body: formData,
        isFormData: true
      });
      
      if (response?.ok) {
        const result = await response.json();
        setImportResult(result);
        setImportProgress({ show: false, message: '', percent: 0 });
        setImportStep('result');
        setCustomColumnMapping({});
        setShowMappingEditor(false);
        showMsg('success', `Importati ${result.imported_matches || 0} risultati!`);
        loadStats();
      } else {
        const error = await response?.json();
        showMsg('error', error?.detail || 'Errore durante l\'importazione');
        setImportProgress({ show: false, message: '', percent: 0 });
        setImportStep('preview');
      }
    } catch (err) {
      console.error('Import execute error:', err);
      showMsg('error', 'Errore durante l\'importazione');
      setImportProgress({ show: false, message: '', percent: 0 });
      setImportStep('preview');
    }
  };

  const resetClubImport = () => {
    setImportFile(null);
    setImportPreview(null);
    setImportStep('upload');
    setImportResult(null);
    setReplaceExistingData(false);
    setCustomColumnMapping({});
    setShowMappingEditor(false);
  };

  const copyCode = (code) => { 
    // Fallback per browser che bloccano Clipboard API
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(code).then(() => {
          showMsg('success', 'Codice copiato!');
        }).catch(() => {
          fallbackCopy(code);
        });
      } else {
        fallbackCopy(code);
      }
    } catch (err) {
      fallbackCopy(code);
    }
  };
  
  const fallbackCopy = (text) => {
    // Crea un elemento temporaneo per copiare
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      showMsg('success', 'Codice copiato!');
    } catch (err) {
      // Se anche questo fallisce, mostra il codice in un prompt
      showMsg('info', `Codice: ${text} (seleziona e copia manualmente)`);
    }
    document.body.removeChild(textArea);
  };

  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full"></div></div>;
  if (!club) return null;

  const trialDays = stats.subscription?.days_remaining || 0;
  
  const menuItems = [
    { id: 'home', icon: '🏠', label: 'Home' },
    { id: 'calendario', icon: '📅', label: 'Calendario' },
    { id: 'tornei-esterni', icon: '🌍', label: 'Tornei Esterni' },
    { id: 'bacheca', icon: '📢', label: 'Bacheca' },
    { id: 'finanze', icon: '💰', label: 'Finanze' },
    { id: 'tornei-v3', icon: '🏆', label: 'Tornei' },
    { id: 'statistiche-v2', icon: '📊', label: 'Statistiche' },
    { id: 'albo-oro', icon: '👑', label: 'Albo d\'Oro' },
    { id: 'partita-singola', icon: '⚽', label: 'Amichevole' },
    { id: 'import-excel', icon: '📥', label: 'Import Excel' },
    { id: 'import-link', icon: '🔗', label: 'Import da Link' },
    { id: 'anagrafica', icon: '📋', label: 'Anagrafica' },
    { id: 'codes', icon: '🎟️', label: 'Codici' },
    { id: 'members', icon: '👥', label: 'Soci Attivi' },
    { id: 'personalizza', icon: '🎨', label: 'Personalizza' },
    { id: 'settings', icon: '⚙️', label: 'Gestione' }
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 text-white
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col
        `}
        style={{ backgroundColor: clubCustomization?.primary_color || '#3B82F6' }}
      >
        {/* Logo/Club Header */}
        <div className="p-4 border-b border-white/20">
          <div className="flex items-center gap-3">
            {clubCustomization?.logo_url ? (
              <img 
                src={clubCustomization.logo_url.startsWith('http') ? clubCustomization.logo_url : `${BACKEND_URL}${clubCustomization.logo_url}`}
                alt="Logo"
                className="w-10 h-10 rounded-full object-cover border-2 border-white/30"
              />
            ) : (
              <span className="text-3xl">🏢</span>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold truncate">{club.name}</h1>
              <p className="opacity-70 text-sm truncate">{club.city || 'Club Subbuteo'}</p>
            </div>
          </div>
          {trialDays > 0 && (
            <div className="mt-3 bg-yellow-500 text-yellow-900 px-3 py-1 rounded-full text-xs text-center">
              ⏳ {trialDays} giorni prova
            </div>
          )}
        </div>
        
        {/* Menu Items */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => { 
                setActiveTab(item.id); 
                if (item.id === 'tornei') setTorneoSelezionato(null);
                if (item.id === 'tornei-v3') setTorneoV3Selezionato(null);
                setSidebarOpen(false);
              }}
              className={`
                w-full flex items-center gap-3 px-4 py-3 text-left
                transition-colors duration-150
                ${activeTab === item.id 
                  ? 'border-r-4 border-white' 
                  : 'hover:bg-white/10'}
              `}
              style={activeTab === item.id ? { backgroundColor: clubCustomization?.secondary_color || '#1E40AF' } : {}}
            >
              <span className="text-xl">{item.icon}</span>
              <span className="font-medium">{item.label}</span>
              {item.badge && (
                <span className="ml-auto px-2 py-0.5 bg-green-500 text-white text-xs rounded-full font-bold">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
        
        {/* Footer */}
        <div className="p-4 border-t border-white/20">
          <button 
            onClick={() => { logoutClub(); navigate('/login-club'); }}
            className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-white/10 transition-colors"
            style={{ backgroundColor: clubCustomization?.secondary_color || '#1E40AF' }}
          >
            <span>🚪</span>
            <span>Esci</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile Header */}
        <header 
          className="lg:hidden text-white px-4 py-3 flex items-center justify-between sticky top-0 z-30"
          style={{ backgroundColor: clubCustomization?.primary_color || '#3B82F6' }}
        >
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-white/10 rounded-lg"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <span className="font-bold">{club.name}</span>
          {clubCustomization?.logo_url ? (
            <img 
              src={clubCustomization.logo_url.startsWith('http') ? clubCustomization.logo_url : `${BACKEND_URL}${clubCustomization.logo_url}`}
              alt="Logo"
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <span className="text-2xl">🏢</span>
          )}
        </header>

        {/* Messages */}
        {msg.text && (
          <div className="px-4 pt-4">
            <div className={`px-4 py-3 rounded-lg ${msg.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {msg.text}
            </div>
          </div>
        )}

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">

        {/* HOME */}
        {/* 🏠 HOME - DASHBOARD POTENZIATA (F20-F22) */}
        {activeTab === 'home' && (
          <div className="space-y-6" data-testid="home-dashboard">
            
            {/* 📣 BANNER STAGIONE */}
            {clubCustomization?.season_banner_visible && clubCustomization?.season_banner_text && (
              <div 
                className="rounded-xl px-4 py-3 text-center text-white font-medium shadow-lg"
                style={{ backgroundColor: clubCustomization?.secondary_color || '#1E40AF' }}
                data-testid="season-banner"
              >
                {clubCustomization.season_banner_text}
              </div>
            )}
            
            {/* HEADER - Nome Club + Città + Stats */}
            <div 
              className="rounded-2xl shadow-xl p-6 text-white"
              style={{
                background: `linear-gradient(135deg, ${clubCustomization?.primary_color || '#3B82F6'} 0%, ${clubCustomization?.secondary_color || '#1E40AF'} 100%)`
              }}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                  {/* Club Logo */}
                  {clubCustomization?.logo_url && (
                    <img 
                      src={clubCustomization.logo_url.startsWith('http') ? clubCustomization.logo_url : `${BACKEND_URL}${clubCustomization.logo_url}`}
                      alt="Logo club"
                      className="w-16 h-16 rounded-full object-cover border-2 border-white/30 shadow-lg"
                      data-testid="dashboard-club-logo"
                    />
                  )}
                  <div>
                    <h1 className="text-3xl font-bold">{club?.name || 'Il Tuo Club'}</h1>
                    <p className="opacity-80 text-lg">{club?.city || 'Club Subbuteo'}</p>
                  </div>
                </div>
                <div className="flex gap-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold">{anagraficaStats.attivi || 0}</p>
                    <p className="opacity-80 text-sm">Soci Attivi</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold">{dashboardWidgets?.quick_stats?.tornei_totali || 0}</p>
                    <p className="opacity-80 text-sm">Tornei</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold">{dashboardWidgets?.quick_stats?.partite_totali || 0}</p>
                    <p className="opacity-80 text-sm">Partite Totali</p>
                  </div>
                </div>
              </div>
            </div>

            {/* GRID CARD COLORATE - Stile Statistiche V2 */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              
              {/* CARD BLU - Torneo Attivo */}
              <div 
                className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl shadow-lg p-5 text-white cursor-pointer hover:shadow-xl transition-all hover:scale-[1.02]"
                onClick={() => {
                  if (dashboardWidgets?.tornei_attivi?.[0]) {
                    const t = dashboardWidgets.tornei_attivi[0];
                    if (t.formato === 'v3') {
                      setTorneoV3Selezionato(t.id);
                      setActiveTab('tornei-v3');
                    } else {
                      setActiveTab('tornei-v3');
                    }
                  } else {
                    setActiveTab('tornei-v3');
                  }
                }}
                data-testid="card-torneo-attivo"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">🏆</span>
                  <h3 className="font-bold text-lg">Torneo Attivo</h3>
                </div>
                {dashboardWidgets?.tornei_attivi?.[0] ? (
                  <div>
                    <p className="font-semibold text-xl mb-2">{dashboardWidgets.tornei_attivi[0].nome}</p>
                    <div className="mb-2">
                      <div className="flex justify-between text-sm text-blue-100 mb-1">
                        <span>Avanzamento</span>
                        <span>{dashboardWidgets.tornei_attivi[0].partite_giocate || 0}/{dashboardWidgets.tornei_attivi[0].partite_totali || 0} partite</span>
                      </div>
                      <div className="w-full bg-blue-900/50 rounded-full h-2">
                        <div 
                          className="bg-white rounded-full h-2 transition-all"
                          style={{width: `${dashboardWidgets.tornei_attivi[0].partite_totali ? (dashboardWidgets.tornei_attivi[0].partite_giocate / dashboardWidgets.tornei_attivi[0].partite_totali) * 100 : 0}%`}}
                        />
                      </div>
                    </div>
                    <p className="text-blue-100 text-sm flex items-center gap-1">
                      Vai al torneo <span>→</span>
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <p className="text-blue-100 mb-2">Nessun torneo attivo</p>
                    <span className="inline-flex items-center gap-1 text-sm bg-white/20 px-3 py-1 rounded-full">
                      + Crea Torneo
                    </span>
                  </div>
                )}
              </div>

              {/* CARD VIOLA - Top 3 Ranking */}
              <div 
                className="bg-gradient-to-br from-purple-500 to-purple-700 rounded-2xl shadow-lg p-5 text-white cursor-pointer hover:shadow-xl transition-all hover:scale-[1.02]"
                onClick={() => setActiveTab('statistiche-v2')}
                data-testid="card-ranking"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">📊</span>
                  <h3 className="font-bold text-lg">Top 3 Ranking</h3>
                </div>
                {dashboardStats?.classifica_stagione?.length > 0 ? (
                  <div className="space-y-2">
                    {dashboardStats.classifica_stagione.slice(0, 3).map((s, i) => (
                      <div key={s.socio_id || i} className="flex items-center justify-between bg-white/10 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}</span>
                          <span className="font-medium text-sm">{s.nome}</span>
                        </div>
                        <span className="font-bold text-sm">{s.punti_ranking_totali} pts</span>
                      </div>
                    ))}
                    <p className="text-purple-100 text-sm flex items-center gap-1 mt-2">
                      Vedi classifica completa <span>→</span>
                    </p>
                  </div>
                ) : (
                  <p className="text-purple-100 text-center py-4">Nessun dato disponibile</p>
                )}
              </div>

              {/* CARD VERDE - Ultima Partita */}
              <div 
                className="bg-gradient-to-br from-green-500 to-emerald-700 rounded-2xl shadow-lg p-5 text-white cursor-pointer hover:shadow-xl transition-all hover:scale-[1.02]"
                onClick={() => {
                  const up = dashboardWidgets?.ultima_partita;
                  if (up?.torneo_id) {
                    // Always use V3 view for matches from tournaments
                    setTorneoV3Selezionato(up.torneo_id);
                    setActiveTab('tornei-v3');
                  } else if (up?.formato === 'amichevole') {
                    setActiveTab('amichevoli');
                  } else {
                    setActiveTab('tornei-v3');
                  }
                }}
                data-testid="card-ultima-partita"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">⚽</span>
                  <h3 className="font-bold text-lg">Ultima Partita</h3>
                </div>
                {dashboardWidgets?.ultima_partita && dashboardWidgets.ultima_partita.giocatore1 && dashboardWidgets.ultima_partita.giocatore2 ? (
                  <div>
                    <div className="bg-white/10 rounded-xl p-3 mb-2">
                      <div className="flex items-center justify-center gap-3 text-center">
                        <div className="flex-1">
                          <p className={`font-semibold text-sm ${dashboardWidgets.ultima_partita.vincitore === dashboardWidgets.ultima_partita.giocatore1 ? 'text-yellow-300' : ''}`}>
                            {dashboardWidgets.ultima_partita.giocatore1}
                          </p>
                        </div>
                        <div className="bg-white/20 px-4 py-2 rounded-lg">
                          <span className="font-bold text-xl">
                            {dashboardWidgets.ultima_partita.score1} - {dashboardWidgets.ultima_partita.score2}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className={`font-semibold text-sm ${dashboardWidgets.ultima_partita.vincitore === dashboardWidgets.ultima_partita.giocatore2 ? 'text-yellow-300' : ''}`}>
                            {dashboardWidgets.ultima_partita.giocatore2}
                          </p>
                        </div>
                      </div>
                    </div>
                    <p className="text-green-100 text-xs">
                      {dashboardWidgets.ultima_partita.torneo_nome}
                      {dashboardWidgets.ultima_partita.data && (
                        <span> • {new Date(dashboardWidgets.ultima_partita.data).toLocaleDateString('it-IT')}</span>
                      )}
                    </p>
                  </div>
                ) : (
                  <p className="text-green-100 text-center py-4">Nessuna partita giocata</p>
                )}
              </div>

              {/* CARD ARANCIONE - Prossimo Evento */}
              <div 
                className="bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl shadow-lg p-5 text-white cursor-pointer hover:shadow-xl transition-all hover:scale-[1.02]"
                onClick={() => setActiveTab('calendario')}
                data-testid="card-prossimo-evento"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">📅</span>
                  <h3 className="font-bold text-lg">Prossimo Evento</h3>
                </div>
                {dashboardWidgets?.prossimi_eventi?.[0] ? (
                  <div>
                    <p className="font-semibold text-lg mb-1">{dashboardWidgets.prossimi_eventi[0].titolo}</p>
                    {dashboardWidgets.prossimi_eventi[0].data && (
                      <div className="flex items-center gap-2 mb-2">
                        <div className="bg-white/20 px-3 py-1 rounded-lg text-center">
                          <p className="text-xs font-bold">{new Date(dashboardWidgets.prossimi_eventi[0].data).toLocaleDateString('it-IT', {day: '2-digit'})}</p>
                          <p className="text-xs uppercase">{new Date(dashboardWidgets.prossimi_eventi[0].data).toLocaleDateString('it-IT', {month: 'short'})}</p>
                        </div>
                        <p className="text-orange-100 text-sm">{dashboardWidgets.prossimi_eventi[0].tipo}</p>
                      </div>
                    )}
                    <p className="text-orange-100 text-sm flex items-center gap-1">
                      Vedi calendario <span>→</span>
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <p className="text-orange-100 mb-2">Nessun evento in programma</p>
                    <span className="inline-flex items-center gap-1 text-sm bg-white/20 px-3 py-1 rounded-full">
                      + Aggiungi Evento
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* CARD BIANCA - Ultimo Annuncio (full width) */}
            <div 
              className="bg-white border border-gray-200 rounded-2xl shadow-lg p-5 cursor-pointer hover:shadow-xl transition-all hover:border-blue-300"
              onClick={() => setActiveTab('bacheca')}
              data-testid="card-ultimo-annuncio"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <span className="text-2xl text-white">📢</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-800 text-lg">Ultimo Annuncio</h3>
                    {dashboardWidgets?.ultimo_annuncio ? (
                      <>
                        <p className="font-semibold text-gray-700">{dashboardWidgets.ultimo_annuncio.title}</p>
                        <p className="text-gray-500 text-sm line-clamp-2">{dashboardWidgets.ultimo_annuncio.content}</p>
                      </>
                    ) : (
                      <p className="text-gray-400 text-sm">Nessun annuncio in bacheca</p>
                    )}
                  </div>
                </div>
                <div className="text-blue-600 font-medium text-sm flex items-center gap-1">
                  Vedi bacheca <span>→</span>
                </div>
              </div>
            </div>

            {/* Hero Widget - Leader & Capocannoniere */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Leader Stagionale */}
              <div 
                className="bg-gradient-to-br from-yellow-400 via-yellow-500 to-orange-500 rounded-2xl shadow-lg p-6 text-white cursor-pointer hover:shadow-xl transition-all transform hover:scale-[1.02]"
                onClick={() => setActiveTab('statistiche-v2')}
                data-testid="widget-leader"
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <span className="text-4xl">👑</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-yellow-100 text-sm font-medium">Leader Stagionale</p>
                    {dashboardWidgets?.leader ? (
                      <>
                        <h3 className="text-2xl font-bold">{dashboardWidgets.leader.nome}</h3>
                        <p className="text-yellow-100">
                          <span className="font-bold text-white">{dashboardWidgets.leader.punti}</span> punti ranking
                          {dashboardWidgets.leader.tornei_vinti > 0 && (
                            <span className="ml-2">• {dashboardWidgets.leader.tornei_vinti}🏆</span>
                          )}
                        </p>
                      </>
                    ) : (
                      <p className="text-yellow-100 text-sm">Nessun dato disponibile</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Capocannoniere */}
              <div 
                className="bg-gradient-to-br from-purple-500 via-purple-600 to-indigo-600 rounded-2xl shadow-lg p-6 text-white cursor-pointer hover:shadow-xl transition-all transform hover:scale-[1.02]"
                onClick={() => setActiveTab('statistiche-v2')}
                data-testid="widget-capocannoniere"
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <span className="text-4xl">⚽</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-purple-200 text-sm font-medium">Capocannoniere</p>
                    {dashboardWidgets?.capocannoniere ? (
                      <>
                        <h3 className="text-2xl font-bold">{dashboardWidgets.capocannoniere.nome}</h3>
                        <p className="text-purple-100">
                          <span className="font-bold text-white">{dashboardWidgets.capocannoniere.gol}</span> gol segnati
                        </p>
                      </>
                    ) : (
                      <p className="text-purple-200 text-sm">Nessun dato disponibile</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <button 
                onClick={() => setActiveTab('tornei-v3')} 
                className="bg-white rounded-xl shadow p-4 hover:shadow-lg transition-all text-left group"
                data-testid="quick-action-tornei"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center group-hover:bg-yellow-200 transition-colors">
                    <span className="text-xl">🏆</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Tornei</p>
                    <p className="text-xs text-gray-500">Gestisci competizioni</p>
                  </div>
                </div>
              </button>
              <button 
                onClick={() => setActiveTab('anagrafica')} 
                className="bg-white rounded-xl shadow p-4 hover:shadow-lg transition-all text-left group"
                data-testid="quick-action-anagrafica"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <span className="text-xl">📋</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Anagrafica</p>
                    <p className="text-xs text-gray-500">Gestisci soci</p>
                  </div>
                </div>
              </button>
              <button 
                onClick={() => setActiveTab('statistiche-v2')} 
                className="bg-white rounded-xl shadow p-4 hover:shadow-lg transition-all text-left group"
                data-testid="quick-action-statistiche"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                    <span className="text-xl">📊</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Statistiche</p>
                    <p className="text-xs text-gray-500">Analizza i dati</p>
                  </div>
                </div>
              </button>
              <button 
                onClick={() => setActiveTab('albo-oro')} 
                className="bg-white rounded-xl shadow p-4 hover:shadow-lg transition-all text-left group"
                data-testid="quick-action-albo-oro"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                    <span className="text-xl">👑</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Albo d'Oro</p>
                    <p className="text-xs text-gray-500">Storico campioni</p>
                  </div>
                </div>
              </button>
            </div>

            {/* 📊 MINI GRAFICI DASHBOARD */}
            <DashboardCharts token={token} />

            {/* Empty State per nuovi club */}
            {!dashboardStats?.soci_stats?.length && !dashboardWidgets?.leader && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 border border-blue-200 rounded-2xl p-8 text-center">
                <div className="text-6xl mb-4">🚀</div>
                <h3 className="font-bold text-xl text-blue-800 mb-2">Benvenuto nel tuo Club!</h3>
                <p className="text-blue-700 text-sm mb-6 max-w-md mx-auto">
                  Inizia aggiungendo soci all'anagrafica e creando il tuo primo torneo. 
                  Le statistiche e classifiche appariranno automaticamente.
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <button 
                    onClick={() => setActiveTab('anagrafica')} 
                    className="bg-blue-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-blue-700 transition-colors"
                  >
                    📋 Aggiungi Soci
                  </button>
                  <button 
                    onClick={() => setActiveTab('tornei-v3')} 
                    className="bg-yellow-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-yellow-600 transition-colors"
                  >
                    🏆 Crea Torneo
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 🌍 TORNEI ESTERNI */}
        {activeTab === 'tornei-esterni' && (
          <div className="space-y-6" data-testid="tornei-esterni-section">
            {/* Header */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">🌍 Tornei Esterni</h2>
                <p className="text-gray-500 text-sm">Tornei e manifestazioni a cui partecipare</p>
              </div>
              <button
                onClick={() => setShowAddTorneoEsterno(true)}
                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-xl font-medium hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg"
                data-testid="add-torneo-esterno-btn"
              >
                + Aggiungi Torneo
              </button>
            </div>

            {/* Lista Tornei */}
            {loadingTorneiEsterni ? (
              <div className="text-center py-12 text-gray-500">Caricamento...</div>
            ) : torneiEsterni.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {torneiEsterni.map(torneo => {
                  const dataTorneo = torneo.data_torneo ? new Date(torneo.data_torneo) : null;
                  const dataIscrizioni = torneo.data_iscrizioni ? new Date(torneo.data_iscrizioni) : null;
                  const oggi = new Date();
                  const isPast = dataTorneo && dataTorneo < oggi;
                  
                  return (
                    <div 
                      key={torneo.id} 
                      className={`bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all ${isPast ? 'opacity-60' : ''}`}
                      data-testid={`torneo-esterno-card-${torneo.id}`}
                    >
                      {/* Preview Image */}
                      {torneo.preview?.image ? (
                        <div className="h-40 bg-gray-200 overflow-hidden">
                          <img 
                            src={torneo.preview.image} 
                            alt={torneo.nome}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      ) : (
                        <div className="h-32 bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                          <span className="text-5xl">🏆</span>
                        </div>
                      )}
                      
                      {/* Content */}
                      <div className="p-4">
                        <h3 className="font-bold text-lg text-gray-800 mb-2">{torneo.nome}</h3>
                        
                        {torneo.preview?.site_name && (
                          <p className="text-xs text-gray-400 mb-2">{torneo.preview.site_name}</p>
                        )}
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2 text-gray-600">
                            <span>📅</span>
                            <span>
                              <strong>Torneo:</strong> {dataTorneo ? dataTorneo.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                            </span>
                          </div>
                          
                          {dataIscrizioni && (
                            <div className="flex items-center gap-2 text-gray-600">
                              <span>📝</span>
                              <span>
                                <strong>Iscrizioni:</strong> dal {dataIscrizioni.toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })}
                              </span>
                            </div>
                          )}
                          
                          {torneo.note && (
                            <p className="text-gray-500 text-xs mt-2 italic">{torneo.note}</p>
                          )}
                        </div>
                        
                        {/* 🎯 IO PARTECIPO - Pulsante + Contatore */}
                        {!isPast && (
                          <div className="mt-4 pt-3 border-t border-dashed">
                            <button
                              onClick={() => handleTogglePartecipazione(torneo.id)}
                              disabled={togglingPartecipazione === torneo.id}
                              className={`w-full py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                                isUserPartecipante(torneo)
                                  ? 'bg-green-100 text-green-700 border-2 border-green-500 hover:bg-green-200'
                                  : 'bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 shadow-lg hover:shadow-xl'
                              }`}
                              data-testid={`btn-partecipo-${torneo.id}`}
                            >
                              {togglingPartecipazione === torneo.id ? (
                                <span className="animate-spin">⏳</span>
                              ) : isUserPartecipante(torneo) ? (
                                <>
                                  <span>✅</span>
                                  <span>Parteciperò!</span>
                                </>
                              ) : (
                                <>
                                  <span>🎯</span>
                                  <span>IO PARTECIPO</span>
                                </>
                              )}
                            </button>
                            
                            {/* Contatore e lista partecipanti */}
                            {torneo.partecipanti && torneo.partecipanti.length > 0 && (
                              <div className="mt-3">
                                <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                                  <span>👥</span>
                                  <span className="font-medium">{torneo.partecipanti.length} {torneo.partecipanti.length === 1 ? 'partecipante' : 'partecipanti'}</span>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {torneo.partecipanti.slice(0, 5).map((p, idx) => (
                                    <span 
                                      key={idx} 
                                      className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs"
                                    >
                                      {p.nome}
                                    </span>
                                  ))}
                                  {torneo.partecipanti.length > 5 && (
                                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                                      +{torneo.partecipanti.length - 5} altri
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Torneo passato - badge */}
                        {isPast && (
                          <div className="mt-4 pt-3 border-t">
                            <div className="bg-gray-100 text-gray-500 px-3 py-2 rounded-lg text-sm text-center">
                              ⏰ Torneo concluso
                              {torneo.partecipanti && torneo.partecipanti.length > 0 && (
                                <span className="ml-2">• {torneo.partecipanti.length} partecipanti</span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Actions */}
                        <div className="mt-3 pt-3 border-t flex justify-between items-center">
                          <a
                            href={torneo.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm"
                          >
                            <span>🔗</span> Vai al sito
                          </a>
                          <button
                            onClick={() => handleDeleteTorneoEsterno(torneo.id)}
                            className="text-red-500 hover:text-red-600 text-sm"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <div className="text-6xl mb-4">🌍</div>
                <h3 className="text-xl font-bold text-gray-700 mb-2">Nessun torneo esterno</h3>
                <p className="text-gray-500 mb-6">Aggiungi tornei e manifestazioni a cui i soci possono partecipare</p>
                <button
                  onClick={() => setShowAddTorneoEsterno(true)}
                  className="bg-blue-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-blue-700"
                >
                  + Aggiungi il primo torneo
                </button>
              </div>
            )}

            {/* Modal Aggiungi Torneo */}
            {showAddTorneoEsterno && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">🌍 Nuovo Torneo Esterno</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nome Torneo *</label>
                      <input
                        type="text"
                        value={torneoEsternoForm.nome}
                        onChange={(e) => setTorneoEsternoForm({...torneoEsternoForm, nome: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Es: Campionato Italiano FISCT"
                        data-testid="torneo-esterno-nome"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Data Torneo *</label>
                      <input
                        type="date"
                        value={torneoEsternoForm.data_torneo}
                        onChange={(e) => setTorneoEsternoForm({...torneoEsternoForm, data_torneo: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        data-testid="torneo-esterno-data"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Data Apertura Iscrizioni</label>
                      <input
                        type="date"
                        value={torneoEsternoForm.data_iscrizioni}
                        onChange={(e) => setTorneoEsternoForm({...torneoEsternoForm, data_iscrizioni: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        data-testid="torneo-esterno-iscrizioni"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Link Sito/Iscrizioni *</label>
                      <input
                        type="url"
                        value={torneoEsternoForm.link}
                        onChange={(e) => setTorneoEsternoForm({...torneoEsternoForm, link: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://..."
                        data-testid="torneo-esterno-link"
                      />
                      <p className="text-xs text-gray-400 mt-1">Verrà estratta automaticamente un'anteprima</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                      <textarea
                        value={torneoEsternoForm.note}
                        onChange={(e) => setTorneoEsternoForm({...torneoEsternoForm, note: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={2}
                        placeholder="Informazioni aggiuntive..."
                        data-testid="torneo-esterno-note"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setShowAddTorneoEsterno(false)}
                      className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Annulla
                    </button>
                    <button
                      onClick={handleAddTorneoEsterno}
                      disabled={savingTorneoEsterno}
                      className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      data-testid="torneo-esterno-save"
                    >
                      {savingTorneoEsterno ? '⏳ Salvataggio...' : '✓ Salva'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 📢 BACHECA */}
        {activeTab === 'bacheca' && (
          <div className="space-y-6">
            {/* Header con tabs Annunci/Feed */}
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold">📢 Bacheca Club</h2>
              <button 
                onClick={() => { setShowNewAnnouncement(true); setEditingAnnouncement(null); setAnnouncementForm({ title: '', content: '', priority: 'normal', is_pinned: false, attachments: [], send_email: false }); }} 
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                + Nuovo Annuncio
              </button>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Colonna Annunci */}
              <div className="lg:col-span-2 space-y-4">
                <h3 className="font-bold text-gray-700 flex items-center gap-2">
                  <span className="text-xl">📋</span> Annunci ({announcements.length})
                </h3>
                
                {/* Form Nuovo/Modifica Annuncio */}
                {(showNewAnnouncement || editingAnnouncement) && (
                  <div className="bg-white rounded-xl shadow p-5 border-l-4 border-blue-500">
                    <h4 className="font-bold mb-4">{editingAnnouncement ? '✏️ Modifica Annuncio' : '📝 Nuovo Annuncio'}</h4>
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Titolo annuncio *"
                        value={announcementForm.title}
                        onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg"
                        data-testid="announcement-title"
                      />
                      <textarea
                        placeholder="Contenuto dell'annuncio *"
                        value={announcementForm.content}
                        onChange={(e) => setAnnouncementForm({...announcementForm, content: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg h-32"
                        data-testid="announcement-content"
                      />
                      <div className="flex flex-wrap gap-4 items-center">
                        <select
                          value={announcementForm.priority}
                          onChange={(e) => setAnnouncementForm({...announcementForm, priority: e.target.value})}
                          className="px-3 py-2 border rounded-lg"
                        >
                          <option value="normal">📄 Normale</option>
                          <option value="important">⚠️ Importante</option>
                          <option value="urgent">🚨 Urgente</option>
                        </select>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={announcementForm.is_pinned}
                            onChange={(e) => setAnnouncementForm({...announcementForm, is_pinned: e.target.checked})}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">📌 Fissa in alto</span>
                        </label>
                      </div>
                      
                      {/* Notifica Email ai Soci */}
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={announcementForm.send_email}
                            onChange={(e) => setAnnouncementForm({...announcementForm, send_email: e.target.checked})}
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className="text-sm">📧 Invia email a tutti i soci</span>
                        </label>
                      </div>
                      
                      {/* Sezione Allegati */}
                      <div className="border-t pt-3">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-sm font-medium text-gray-700">📎 Allegati ({announcementForm.attachments?.length || 0}/3)</span>
                          {(announcementForm.attachments?.length || 0) < 3 && (
                            <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-lg text-sm flex items-center gap-1">
                              <input
                                type="file"
                                multiple
                                accept="image/*,.pdf,.doc,.docx"
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploadingFiles}
                              />
                              {uploadingFiles ? '⏳ Caricamento...' : '+ Aggiungi file'}
                            </label>
                          )}
                        </div>
                        {announcementForm.attachments?.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {announcementForm.attachments.map((file, idx) => (
                              <div key={idx} className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg text-sm">
                                {file.is_image ? '🖼️' : '📄'}
                                <span className="max-w-[150px] truncate">{file.filename}</span>
                                <button 
                                  onClick={() => removeAttachment(idx)}
                                  className="text-red-500 hover:text-red-700"
                                >✕</button>
                              </div>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-gray-400 mt-1">Max 5MB per file • Immagini, PDF, Word</p>
                      </div>
                      
                      <div className="flex gap-2">
                        <button 
                          onClick={editingAnnouncement ? updateAnnouncement : createAnnouncement}
                          disabled={savingAnnouncement || uploadingFiles}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                          data-testid="announcement-submit"
                        >
                          {savingAnnouncement ? 'Salvataggio...' : (editingAnnouncement ? 'Aggiorna' : 'Pubblica')}
                        </button>
                        <button 
                          onClick={() => { setShowNewAnnouncement(false); setEditingAnnouncement(null); setAnnouncementForm({ title: '', content: '', priority: 'normal', is_pinned: false, attachments: [], send_email: false }); }}
                          className="bg-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Lista Annunci */}
                {announcements.length === 0 ? (
                  <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
                    <div className="text-4xl mb-3">📢</div>
                    <p>Nessun annuncio pubblicato</p>
                    <p className="text-sm mt-1">Pubblica il primo annuncio per i tuoi soci!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {announcements
                      .sort((a, b) => (b.is_pinned ? 1 : 0) - (a.is_pinned ? 1 : 0))
                      .map(ann => (
                      
                      // CARD EVENTO con LOCANDINA GRANDE
                      ann.tipo_annuncio === 'evento' ? (
                        <EventoCardBacheca 
                          key={ann.id}
                          annuncio={ann}
                          onEdit={() => { 
                            setEditingAnnouncement(ann); 
                            setAnnouncementForm({ title: ann.title, content: ann.content, priority: ann.priority, is_pinned: ann.is_pinned, attachments: ann.attachments || [], send_email: false }); 
                            setShowNewAnnouncement(false);
                          }}
                          onDelete={() => deleteAnnouncement(ann.id)}
                          clubId={club?.id}
                          userNome={club?.name || 'Utente'}
                        />
                      ) : (
                      
                      // CARD ANNUNCIO NORMALE
                      <div 
                        key={ann.id} 
                        className={`bg-white rounded-xl shadow p-5 border-l-4 ${
                          ann.priority === 'urgent' ? 'border-red-500 bg-red-50' :
                          ann.priority === 'important' ? 'border-yellow-500 bg-yellow-50' :
                          'border-gray-200'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {ann.is_pinned && <span className="text-sm">📌</span>}
                              {ann.priority === 'urgent' && <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded">URGENTE</span>}
                              {ann.priority === 'important' && <span className="bg-yellow-500 text-white text-xs px-2 py-0.5 rounded">IMPORTANTE</span>}
                            </div>
                            <h4 className="font-bold text-lg">{ann.title}</h4>
                            <p className="text-gray-600 mt-2 whitespace-pre-wrap">{ann.content}</p>
                            
                            {/* Allegati */}
                            {ann.attachments && ann.attachments.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {ann.attachments.map((file, idx) => (
                                  <a 
                                    key={idx}
                                    href={`${BACKEND_URL}${file.url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg text-sm transition-colors"
                                  >
                                    {file.is_image ? (
                                      <img 
                                        src={`${BACKEND_URL}${file.url}`} 
                                        alt={file.filename}
                                        className="w-8 h-8 object-cover rounded"
                                      />
                                    ) : (
                                      <span>📄</span>
                                    )}
                                    <span className="max-w-[120px] truncate text-blue-600">{file.filename}</span>
                                  </a>
                                ))}
                              </div>
                            )}
                            
                            <p className="text-xs text-gray-400 mt-3">
                              {ann.author} • {new Date(ann.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              {ann.attachments?.length > 0 && ` • 📎 ${ann.attachments.length} allegati`}
                            </p>
                          </div>
                          <div className="flex gap-1 ml-3">
                            <button 
                              onClick={() => { 
                                setEditingAnnouncement(ann); 
                                setAnnouncementForm({ title: ann.title, content: ann.content, priority: ann.priority, is_pinned: ann.is_pinned, attachments: ann.attachments || [], send_email: false }); 
                                setShowNewAnnouncement(false);
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                              title="Modifica"
                            >
                              ✏️
                            </button>
                            <button 
                              onClick={() => deleteAnnouncement(ann.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title="Elimina"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      </div>
                      )
                    ))}
                  </div>
                )}
              </div>

              {/* Colonna Feed Attività */}
              <div className="space-y-4">
                <h3 className="font-bold text-gray-700 flex items-center gap-2">
                  <span className="text-xl">📰</span> Attività Recenti
                </h3>
                
                {activityFeed.length === 0 ? (
                  <div className="bg-white rounded-xl shadow p-6 text-center text-gray-500">
                    <div className="text-3xl mb-2">📰</div>
                    <p className="text-sm">Nessuna attività recente</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow divide-y max-h-[600px] overflow-y-auto">
                    {activityFeed.map(activity => (
                      <div key={activity.id} className="p-4 hover:bg-gray-50 group">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{activity.title}</p>
                            <p className="text-xs text-gray-500 mt-1">{activity.description}</p>
                            <p className="text-xs text-gray-400 mt-2">
                              {new Date(activity.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          <button 
                            onClick={() => deleteActivity(activity.id)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500"
                            title="Rimuovi"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 🏆 TORNEI - SISTEMA V3 */}
        {activeTab === 'tornei-v3' && (
          <div className="space-y-6">
            {importedTournamentId ? (
              <ImportedTournamentDetail
                token={token}
                tournamentId={importedTournamentId}
                onBack={() => setImportedTournamentId(null)}
                onRefresh={() => setImportedTournamentId(null)}
              />
            ) : !torneoV3Selezionato ? (
              <TorneiV3List 
                token={token}
                onSelectTournament={(id) => setTorneoV3Selezionato(id)}
                onSelectImportedTournament={(id) => setImportedTournamentId(id)}
                onImport={() => setActiveTab('import-excel')}
                onImportLink={() => setActiveTab('import-link')}
              />
            ) : (
              <TorneoDettaglioV3 
                torneoId={torneoV3Selezionato}
                token={token}
                onBack={() => setTorneoV3Selezionato(null)}
                onRefresh={loadDashboardStats}
              />
            )}
          </div>
        )}

        {/* 📅 CALENDARIO */}
        {activeTab === 'calendario' && (
          <div className="space-y-6">
            <CalendarioClub 
              token={token}
              onRefresh={loadDashboardStats}
            />
          </div>
        )}

        {/* 📊 STATISTICHE V2 */}
        {activeTab === 'statistiche-v2' && (
          <div className="space-y-6">
            <DashboardStatsClubV2 
              token={token}
              onSelectGiocatore={(nome, socioId) => setShowSchedaGiocatore({ nome, socioId })}
            />
          </div>
        )}

        {/* 👑 ALBO D'ORO - F7 */}
        {activeTab === 'albo-oro' && (
          <div className="space-y-6">
            <AlboOro 
              token={token}
              onSelectGiocatore={(nome, socioId) => setShowSchedaGiocatore({ nome, socioId })}
              onSelectTorneo={(torneoId, isImported) => {
                if (isImported) {
                  setImportedTournamentId(torneoId);
                } else {
                  setTorneoV3Selezionato(torneoId);
                }
                setActiveTab('tornei-v3');
              }}
            />
          </div>
        )}

        {/* 💰 FINANZE */}
        {activeTab === 'finanze' && (
          <div className="space-y-6">
            <FinanzeClub 
              token={token}
              onRefresh={loadDashboardStats}
            />
          </div>
        )}

        {/* ⚽ PARTITA SINGOLA - Nuovo Sistema */}
        {activeTab === 'partita-singola' && (
          <PartitaSingola token={token} onRefresh={loadDashboardStats} />
        )}

        {/* 📥 IMPORT TORNEO CON AI */}
        {activeTab === 'import-excel' && (
          <ImportTorneoAI 
            token={token} 
            clubId={club?.id}
            showMsg={showMsg}
            onSuccess={(tournamentId) => {
              loadStats();
              loadDashboardStats();
              setActiveTab('tornei-v3');
            }}
            onCancel={() => setActiveTab('home')}
          />
        )}

        {/* 🔗 IMPORT TORNEO DA LINK */}
        {activeTab === 'import-link' && (
          <ImportTorneoFromLink 
            token={token} 
            onSuccess={(tournamentId) => {
              loadStats();
              loadDashboardStats();
              loadAnagrafica();
              loadTornei();
              setActiveTab('tornei-v3');
            }}
            onCancel={() => setActiveTab('home')}
          />
        )}

        {/* ANAGRAFICA SOCI */}
        {activeTab === 'anagrafica' && (
          <div className="space-y-6">
            {/* Add Socio Button */}
            {!showAddSocio && (
              <button onClick={() => { setEditingSocio(null); setShowAddSocio(true); }} className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-medium">
                ➕ Aggiungi Socio
              </button>
            )}

            {/* Add/Edit Socio Form */}
            {showAddSocio && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold mb-4">{editingSocio ? '✏️ Modifica Socio' : '➕ Nuovo Socio'}</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                    <input type="text" value={socioForm.nome} onChange={e => setSocioForm({...socioForm, nome: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg" placeholder="Mario" data-testid="socio-nome" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
                    <input type="text" value={socioForm.cognome} onChange={e => setSocioForm({...socioForm, cognome: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg" placeholder="Rossi" data-testid="socio-cognome" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">🏷️ Nickname</label>
                    <input type="text" value={socioForm.nickname} onChange={e => setSocioForm({...socioForm, nickname: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg" placeholder="Il Bomber, Mago..." data-testid="socio-nickname" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input type="email" value={socioForm.email} onChange={e => setSocioForm({...socioForm, email: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg" placeholder="mario@email.com" data-testid="socio-email" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                    <input type="text" value={socioForm.telefono} onChange={e => setSocioForm({...socioForm, telefono: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg" placeholder="+39 333..." data-testid="socio-telefono" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">N° Tessera</label>
                    <input type="text" value={socioForm.numero_tessera} onChange={e => setSocioForm({...socioForm, numero_tessera: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg" placeholder="001" data-testid="socio-tessera" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ruolo</label>
                    <select value={socioForm.ruolo} onChange={e => setSocioForm({...socioForm, ruolo: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg" data-testid="socio-ruolo">
                      <option value="socio">👁️ Socio (visualizzazione)</option>
                      <option value="admin">🔑 Admin (modifica)</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                    <textarea value={socioForm.note} onChange={e => setSocioForm({...socioForm, note: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg" rows="2" placeholder="Note opzionali..." data-testid="socio-note" />
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <button 
                    onClick={editingSocio ? updateSocio : addSocio} 
                    disabled={addingSocio} 
                    className={`px-6 py-2 text-white rounded-lg disabled:bg-gray-400 ${editingSocio ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}`} 
                    data-testid="add-socio-btn"
                  >
                    {addingSocio ? '⏳ Salvataggio...' : (editingSocio ? '💾 Aggiorna Socio' : '💾 Salva Socio')}
                  </button>
                  <button onClick={cancelEditSocio} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Annulla</button>
                </div>

                {/* Show generated code - only for new socio */}
                {newSocioCode && !editingSocio && (
                  <div className="mt-4 p-4 bg-green-50 border-2 border-green-300 rounded-lg">
                    <p className="font-bold text-green-800">✅ Codice generato per il socio:</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-2xl font-mono font-bold bg-white px-4 py-2 rounded border">{newSocioCode}</span>
                      <button onClick={() => copyCode(newSocioCode)} className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">📋 Copia</button>
                    </div>
                    <p className="text-sm text-green-700 mt-2">Condividi questo codice con il socio per permettergli di attivare la sua area personale.</p>
                  </div>
                )}
              </div>
            )}

            {/* Anagrafica List */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">📋 Anagrafica Soci ({anagrafica.length})</h3>
                <div className="text-sm text-gray-500">
                  Attivi: {anagraficaStats.attivi || 0} | In attesa: {anagraficaStats.in_attesa || 0}
                </div>
              </div>

              {anagrafica.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Nessun socio in anagrafica. Clicca "Aggiungi Socio" per iniziare.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Nome</th>
                        <th className="px-3 py-2 text-left">🏷️ Nickname</th>
                        <th className="px-3 py-2 text-left">Email</th>
                        <th className="px-3 py-2 text-left">Telefono</th>
                        <th className="px-3 py-2 text-left">Tessera</th>
                        <th className="px-3 py-2 text-left">Ruolo</th>
                        <th className="px-3 py-2 text-left">Stato</th>
                        <th className="px-3 py-2 text-left">Codice</th>
                        <th className="px-3 py-2 text-left">Azioni</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {anagrafica.map(s => (
                        <tr key={s.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{s.nome_completo}</td>
                          <td className="px-3 py-2 text-purple-600 font-medium">{s.nickname || <span className="text-gray-300">-</span>}</td>
                          <td className="px-3 py-2 text-gray-600">{s.email || '-'}</td>
                          <td className="px-3 py-2 text-gray-600">{s.telefono || '-'}</td>
                          <td className="px-3 py-2">{s.numero_tessera || '-'}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${s.ruolo === 'admin' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                              {s.ruolo === 'admin' ? '🔑 Admin' : '👁️ Socio'}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${s.status === 'attivo' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {s.status === 'attivo' ? '✅ Attivo' : '⏳ In attesa'}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            {s.codice_accesso ? (
                              <div className="flex items-center gap-1">
                                <code className="bg-gray-100 px-2 py-0.5 rounded text-xs font-mono">{s.codice_accesso}</code>
                                <button onClick={() => copyCode(s.codice_accesso)} className="text-blue-600 hover:text-blue-800">📋</button>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs italic">Nessun codice</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex gap-1">
                              <button onClick={() => startEditSocio(s)} className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1 rounded hover:bg-blue-50" title="Modifica dati">✏️</button>
                              {/* Show generate code button for soci without code OR regenerate for those with code */}
                              {!s.codice_accesso ? (
                                <button onClick={() => regenerateCode(s.id, s.nome_completo, false)} className="text-green-600 hover:text-green-800 text-xs px-2 py-1 rounded hover:bg-green-50" title="Genera codice per app">📱</button>
                              ) : s.status === 'in_attesa' && (
                                <button onClick={() => regenerateCode(s.id, s.nome_completo, true)} className="text-orange-600 hover:text-orange-800 text-xs px-2 py-1 rounded hover:bg-orange-50" title="Rigenera codice">🔄</button>
                              )}
                              <button onClick={() => removeSocio(s.id, s.nome_completo)} className="text-red-600 hover:text-red-800 text-xs px-2 py-1 rounded hover:bg-red-50" title="Rimuovi">🗑️</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CODES - Quick generation */}
        {activeTab === 'codes' && (
          <div className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 text-sm">
                💡 <strong>Consiglio:</strong> Usa la sezione "Anagrafica Soci" per aggiungere soci con i loro dati. 
                Qui puoi generare codici generici se necessario.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold mb-4">🎟️ Genera Codici Generici</h3>
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo</label>
                  <select value={codeType} onChange={e => setCodeType(e.target.value)} className="px-3 py-2 border rounded-lg">
                    <option value="socio">👁️ Socio</option>
                    <option value="admin">🔑 Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Quantità</label>
                  <input type="number" min="1" max="20" value={codeQty} onChange={e => setCodeQty(Math.min(20, Math.max(1, +e.target.value)))} className="w-20 px-3 py-2 border rounded-lg" />
                </div>
                <button onClick={generateCodes} disabled={generating} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400">
                  {generating ? '⏳' : '✨ Genera'}
                </button>
              </div>

              {newCodes.length > 0 && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="font-bold text-green-800 mb-2">Codici generati:</p>
                  <div className="flex flex-wrap gap-2">
                    {newCodes.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 bg-white px-3 py-1 rounded border">
                        <code className="font-mono font-bold">{c.code}</code>
                        <button onClick={() => copyCode(c.code)} className="text-blue-600">📋</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Codes list */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold mb-4">📜 Tutti i Codici ({codes.length})</h3>
              {codes.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Nessun codice</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Codice</th>
                        <th className="px-3 py-2 text-left">Per</th>
                        <th className="px-3 py-2 text-left">Tipo</th>
                        <th className="px-3 py-2 text-left">Stato</th>
                        <th className="px-3 py-2 text-left">Usato da</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {codes.slice(0, 50).map(c => (
                        <tr key={c.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono">{c.code}</td>
                          <td className="px-3 py-2">{c.socio_nome || <span className="text-gray-400">Generico</span>}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${c.type === 'admin' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                              {c.type === 'admin' ? '🔑' : '👁️'} {c.type}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${c.status === 'active' ? 'bg-green-100 text-green-700' : c.status === 'used' ? 'bg-gray-100 text-gray-600' : 'bg-red-100 text-red-600'}`}>
                              {c.status === 'active' ? '✅' : c.status === 'used' ? '✓' : '✗'} {c.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-600">{c.used_by_name || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* MEMBERS */}
        {activeTab === 'members' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold mb-4">👥 Soci con Account Attivo ({members.length})</h3>
            {members.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Nessun socio ha ancora attivato l'account</p>
                <p className="text-sm text-gray-400 mt-2">I soci appariranno qui dopo aver usato il loro codice</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Nome</th>
                      <th className="px-3 py-2 text-left">Email</th>
                      <th className="px-3 py-2 text-left">Ruolo</th>
                      <th className="px-3 py-2 text-left">Iscritto</th>
                      <th className="px-3 py-2 text-left">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {members.map(m => (
                      <tr key={m.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{m.anagrafica_nome || m.user_name}</td>
                        <td className="px-3 py-2 text-gray-600">{m.user_email}</td>
                        <td className="px-3 py-2">
                          <select value={m.role} onChange={e => updateMemberRole(m.id, e.target.value)} className="px-2 py-1 border rounded text-xs">
                            <option value="socio">👁️ Socio</option>
                            <option value="admin">🔑 Admin</option>
                          </select>
                        </td>
                        <td className="px-3 py-2 text-gray-500">{new Date(m.joined_at).toLocaleDateString('it-IT')}</td>
                        <td className="px-3 py-2">
                          <button onClick={() => removeMember(m.id, m.user_name)} className="text-red-600 hover:text-red-800 text-xs">🗑️</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* PERSONALIZZA CLUB */}
        {activeTab === 'personalizza' && (
          <PersonalizzaClub 
            token={token} 
            club={club}
            showMsg={showMsg}
            onSave={(customization) => {
              // Refresh customization data to update sidebar/header
              loadClubCustomization();
              refreshClubData();
            }}
          />
        )}

        {/* SETTINGS */}
        {activeTab === 'settings' && (
          <div className="space-y-6">

            {/* Impostazioni Club */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">⚙️ Impostazioni Club</h3>
                {!editSettings && <button onClick={() => setEditSettings(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">✏️ Modifica</button>}
              </div>
              {editSettings ? (
                <div className="space-y-4">
                  <input type="text" placeholder="Nome Club" value={settingsForm.club_name} onChange={e => setSettingsForm({...settingsForm, club_name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                  <input type="text" placeholder="Città" value={settingsForm.club_city} onChange={e => setSettingsForm({...settingsForm, club_city: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                  <textarea placeholder="Descrizione" value={settingsForm.club_description} onChange={e => setSettingsForm({...settingsForm, club_description: e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows="3" />
                  <div className="flex gap-3">
                    <button onClick={saveSettings} disabled={savingSettings} className="px-4 py-2 bg-green-600 text-white rounded-lg">{savingSettings ? '⏳' : '💾 Salva'}</button>
                    <button onClick={() => setEditSettings(false)} className="px-4 py-2 bg-gray-200 rounded-lg">Annulla</button>
                  </div>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded"><span className="text-gray-500 text-sm">Nome</span><p className="font-medium">{club.name}</p></div>
                  <div className="p-3 bg-gray-50 rounded"><span className="text-gray-500 text-sm">Città</span><p className="font-medium">{club.city || '-'}</p></div>
                  <div className="p-3 bg-gray-50 rounded"><span className="text-gray-500 text-sm">Username</span><p className="font-mono">{club.username}</p></div>
                  <div className="p-3 bg-gray-50 rounded"><span className="text-gray-500 text-sm">Email</span><p>{club.email}</p></div>
                </div>
              )}
            </div>

            {/* 🌐 Pagina Pubblica Club */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold mb-4">🌐 Pagina Pubblica</h3>
              <p className="text-sm text-gray-600 mb-4">
                Attiva la pagina pubblica del club per condividere le statistiche e le classifiche con tutti. 
                Il link sarà visibile a chiunque, anche senza login.
              </p>
              
              <div className="flex items-center justify-between p-4 rounded-lg border-2 border-gray-200">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{club.is_public ? '✅' : '🔒'}</span>
                  <div>
                    <div className="font-medium">Pagina Pubblica</div>
                    <div className="text-sm text-gray-500">
                      {club.is_public ? 'Visibile a tutti' : 'Solo i soci possono vedere'}
                    </div>
                  </div>
                </div>
                <button
                  onClick={toggleClubPublic}
                  disabled={togglingClubPublic}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    club.is_public
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {togglingClubPublic ? '...' : club.is_public ? 'Disattiva' : 'Attiva'}
                </button>
              </div>
              
              {club.is_public && (
                <div className="mt-4 p-3 sm:p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="space-y-3">
                    <div>
                      <div className="text-sm text-green-700 font-medium">Link pubblico del club:</div>
                      <div className="text-green-800 font-mono text-xs sm:text-sm mt-1 break-all">
                        {window.location.origin}/club/public/{club.username}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/club/public/${club.username}`);
                          showMsg('success', 'Link copiato!');
                        }}
                        className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                      >
                        📋 Copia
                      </button>
                      <button
                        onClick={() => window.open(`/club/public/${club.username}`, '_blank')}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                      >
                        👁️ Anteprima
                      </button>
                    </div>
                  </div>
                  
                  {/* Pulsanti Social */}
                  <div className="mt-4 pt-4 border-t border-green-200">
                    <div className="text-sm text-green-700 mb-3">Condividi su:</div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          const shareUrl = `${window.location.origin}/api/share/club/${club.username}`;
                          window.open(`https://wa.me/?text=${encodeURIComponent(`Guarda le statistiche del nostro club! ${shareUrl}`)}`, '_blank');
                        }}
                        className="px-3 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 flex items-center gap-1"
                      >
                        📱 WhatsApp
                      </button>
                      <button
                        onClick={() => {
                          const shareUrl = `${window.location.origin}/api/share/club/${club.username}`;
                          window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
                        }}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-1"
                      >
                        📘 Facebook
                      </button>
                      <button
                        onClick={() => {
                          const url = `${window.location.origin}/club/public/${club.username}`;
                          navigator.clipboard.writeText(url);
                          showMsg('success', 'Link copiato! Incollalo su Instagram 📸');
                          // Apri Instagram se su mobile
                          const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                          if (isMobile) {
                            setTimeout(() => window.open('instagram://app', '_blank'), 500);
                          }
                        }}
                        className="px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-sm hover:from-purple-600 hover:to-pink-600 flex items-center gap-1"
                      >
                        📸 Instagram
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold mb-4">💳 Abbonamento</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-sm text-gray-500">Piano</p>
                  <p className="font-bold text-blue-700 capitalize">{stats.subscription?.plan || 'Trial'}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-sm text-gray-500">Stato</p>
                  <p className="font-bold text-green-700 capitalize">{stats.subscription?.status || 'Attivo'}</p>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg text-center">
                  <p className="text-sm text-gray-500">Giorni</p>
                  <p className="font-bold text-yellow-700">{trialDays > 0 ? trialDays : '∞'}</p>
                </div>
              </div>
              
              {/* Avviso Trial Scaduto */}
              {stats.subscription?.plan === 'trial' && trialDays <= 0 && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">⚠️</span>
                    <div>
                      <p className="font-bold text-red-800">Periodo di prova scaduto!</p>
                      <p className="text-sm text-red-600">Passa a Premium per continuare a usare tutte le funzionalità.</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Pulsante Upgrade Premium */}
              {stats.subscription?.plan !== 'premium' && (
                <div className="mt-4 pt-4 border-t">
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 rounded-xl font-bold hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg"
                    data-testid="club-upgrade-premium-btn"
                  >
                    💎 Passa a Premium - €35/anno
                  </button>
                  <p className="text-xs text-center text-gray-500 mt-2">Sblocca tutte le funzionalità avanzate</p>
                </div>
              )}
            </div>
          </div>
        )}

        </main>

        {/* Modal Upgrade Premium Club */}
        {showUpgradeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="text-center mb-6">
                <div className="text-5xl mb-3">💎</div>
                <h2 className="text-2xl font-bold text-gray-800">Club Premium</h2>
                <p className="text-gray-500">Sblocca tutte le funzionalità</p>
              </div>
              
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 mb-6">
                <div className="text-center">
                  <span className="text-4xl font-bold text-green-600">€35</span>
                  <span className="text-gray-500">/anno</span>
                </div>
              </div>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-sm">
                  <span className="text-green-500">✓</span> Soci illimitati
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <span className="text-green-500">✓</span> Tornei illimitati
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <span className="text-green-500">✓</span> Statistiche avanzate
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <span className="text-green-500">✓</span> Grafici interattivi
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <span className="text-green-500">✓</span> Albo d'oro e archivio
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <span className="text-green-500">✓</span> Supporto prioritario
                </li>
              </ul>
              
              <div className="space-y-3">
                <button
                  onClick={handleClubPayment}
                  disabled={loadingPayment}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 rounded-xl font-bold hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg disabled:opacity-50"
                  data-testid="club-paypal-btn"
                >
                  {loadingPayment ? '⏳ Elaborazione...' : '💳 Paga con PayPal'}
                </button>
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="w-full py-2 text-gray-500 hover:text-gray-700"
                >
                  Annulla
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Scheda Giocatore V2 */}
        {showSchedaGiocatore && (
          <SchedaGiocatoreV2 
            nomeGiocatore={showSchedaGiocatore.nome}
            socioId={showSchedaGiocatore.socioId}
            token={token}
            onClose={() => setShowSchedaGiocatore(null)}
          />
        )}
      </div>
    </div>
  );
};

export default { ClubAuthProvider, ClubLoginForm, ClubRegisterForm, ClubDashboard };
